// Import React tools
import React, { useContext, useEffect, useMemo, useState, } from "react";

// Import context function
import { AuthContext } from "global/context/AuthContext";
import { UserContext } from "global/context/UserContext";

// Import strich api calls
import {
    calculateOrderEarnings,
    getPodProductPrices,
} from "api/APIStrichCalls";

// Import components
import CreateButton from "global/components/CreateButton";
import { InfoButton } from "global/components/InfoButton";
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";
import { FormatCurrency } from "global/utilities/ConvertCurrency";

// Import styles
import "./ProductPriceEditor.css";
import { CloseIcon } from "global/strich_icons/StrichIcons";



// This stores the available price modes
const PRICE_MODES = {

    // This means use the recommended retail price
    RRP: "D",

    // This means use a locked baseline price
    BASELINE: "B",

    // This means adjust the RRP by a multiplier
    MODIFIER: "M",

    // This means target a minimum profit amount
    PROFIT: "P",

};



// This stores the API preview status values
const EARNINGS_PREVIEW_STATUS = {
    IDLE: "idle",
    LOADING: "loading",
    READY: "ready",
    ERROR: "error",
};



// This maps local price mode to the API mock-pricing method
export const getApiPricingMethodFromPriceConfig = (priceConfig) => {

    // Gets the pricing method understood by orders/order?calc_earnings=Y

    // Read the active mode
    const activeMode = priceConfig?.mode || PRICE_MODES.RRP;

    // RRP/default pricing should use the product's normal resolved price
    if (activeMode === PRICE_MODES.RRP) { return ""; }

    // Fixed baseline price
    if (activeMode === PRICE_MODES.BASELINE) { return "fixed"; }

    // Percentage adjustment from RRP
    if (activeMode === PRICE_MODES.MODIFIER) { return "adjust_percent"; }

    // Minimum profit target
    if (activeMode === PRICE_MODES.PROFIT) { return "min_profit"; }

    // Return blank for unknown modes
    return "";

};



// This maps local price mode value to the API mock-pricing value
export const getApiPricingValueFromPriceConfig = (priceConfig) => {

    // Gets the pricing value understood by orders/order?calc_earnings=Y

    // Read the active mode
    const activeMode = priceConfig?.mode || PRICE_MODES.RRP;

    // RRP/default pricing needs no value
    if (activeMode === PRICE_MODES.RRP) { return ""; }

    // Baseline and minimum profit values are passed through as money values
    if (activeMode === PRICE_MODES.BASELINE || activeMode === PRICE_MODES.PROFIT) {
        return priceConfig?.value || "";
    }

    // Modifier mode stores a multiplier locally, but the API wants a percentage
    if (activeMode === PRICE_MODES.MODIFIER) {
        return Math.round((toNumber(priceConfig?.value || 1) * 100) - 100);
    }

    // Return blank for unknown modes
    return "";

};



// This builds one mock order item for the API earnings preview
export const buildEarningsPreviewOrderItem = ({
    currencyCode,
    storeSku,
    podProductSku,
    designSetId,
    priceConfig,
    colourwayCode = "",
    venueOrderItemId = Date.now(),
}) => {

    // Get the API pricing controls
    const pricingMethod = getApiPricingMethodFromPriceConfig(priceConfig);
    const pricingValue = getApiPricingValueFromPriceConfig(priceConfig);

    // Build the order item config
    const orderItemConfig = {
        pod_product_sku: podProductSku,
    };

    // Add the default colourway when the design product has one
    if (String(colourwayCode || "").trim() !== "") {
        orderItemConfig.colourway_code = String(colourwayCode).trim();
    }

    // Build the order item
    const orderItem = {
        sku: storeSku,
        orderquantity: 1,
        venueorderitemid: venueOrderItemId,
        currency: currencyCode,
        order_item_config: JSON.stringify(orderItemConfig),
        pod_product_sku: podProductSku,
        design_set_id: designSetId,
    };

    // Add pricing controls when one is selected
    if (pricingMethod) {
        orderItem.pricing_method = pricingMethod;
        orderItem.pricing_value = pricingValue;
    }

    // Return the mock order item
    return orderItem;

};



// This builds the mock order payload for the API earnings preview
const buildEarningsPreviewOrderPayload = ({
    connectionId,
    deliveryCountry,
    shippingMethodCode,
    currencyCode,
    storeSku,
    podProductSku,
    designSetId,
    priceConfig,
    colourwayCode = "",
}) => {

    // Build the mock order id
    const venueOrderId = Date.now();

    // Return the mock order payload
    return {
        connid: connectionId,
        venueorderid: venueOrderId,
        deliverycountry: deliveryCountry,
        billingcountry: deliveryCountry,
        venueshippingmethod: shippingMethodCode,
        currency: currencyCode,
        status: "mock",
        orderitems: [
            buildEarningsPreviewOrderItem({
                currencyCode,
                storeSku,
                podProductSku,
                designSetId,
                priceConfig,
                colourwayCode,
                venueOrderItemId: venueOrderId + 1,
            }),
        ],
    };

};



// This safely converts unknown values into numbers
const toNumber = (value) => {

    // Convert the value into a number
    const numericValue = Number(value);

    // Return the numeric value when valid, otherwise zero
    return Number.isFinite(numericValue) ? numericValue : 0;

};



// This checks if a value should be treated as blank/null
const isBlankValue = (value) => {

    // Return true when value is null or undefined
    if (value === null || value === undefined) return true;

    // Return true when value is an empty string
    return String(value).trim() === "";

};



// This cleans a money input value
const cleanMoneyInput = (value) => {

    // Strip anything except digits and decimal points
    const cleanedValue = String(value || "").replace(/[^0-9.]/g, "");

    // Split the value by decimal points
    const valueParts = cleanedValue.split(".");

    // Return the cleaned integer value when there is no decimal
    if (valueParts.length === 1) return valueParts[0];

    // Return the cleaned value with two decimal places
    return `${valueParts[0]}.${(valueParts[1] || "").slice(0, 2)}`;

};



// This cleans a percentage adjustment input value
const cleanPercentInput = (value) => {

    // Convert the value into a string
    const stringValue = String(value || "");

    // Check whether the first character is a minus sign
    const hasMinusSign = stringValue.trim().startsWith("-");

    // Check whether the first character is a plus sign
    const hasPlusSign = stringValue.trim().startsWith("+");

    // Strip everything except digits
    const digitsOnlyValue = stringValue.replace(/[^0-9]/g, "");

    // Return just the minus sign while the user is typing
    if (hasMinusSign && digitsOnlyValue === "") return "-";

    // Return just the plus sign while the user is typing
    if (hasPlusSign && digitsOnlyValue === "") return "+";

    // Return the negative whole number
    if (hasMinusSign) return `-${digitsOnlyValue}`;

    // Return the positive whole number with a plus sign
    if (hasPlusSign) return `+${digitsOnlyValue}`;

    // Return the cleaned whole number
    return digitsOnlyValue;

};



// This rounds a value up to the nearest penny
const roundUpToNearestPenny = (value) => {

    // Convert the incoming value into a safe number
    const safeValue = toNumber(value);

    // Return zero when the value is zero or lower
    if (safeValue <= 0) return 0;

    // Round up to the nearest penny
    return Math.ceil(safeValue * 100) / 100;

};



// This rounds a value to the nearest penny
const roundToNearestPenny = (value) => {

    // Convert the incoming value into a safe number
    const safeValue = toNumber(value);

    // Round using normal currency rounding
    return Math.round(safeValue * 100) / 100;

};



// This gets currency metadata safely
const getCurrencyMeta = (coreCurrencies, currencyCode = "GBP") => {

    // Normalize the currency code
    const safeCurrencyCode = String(currencyCode || "GBP").toUpperCase();

    // Return the matched currency metadata
    return coreCurrencies?.[safeCurrencyCode] || coreCurrencies?.GBP || {};

};



// This gets a currency exchange rate against GBP
const getCurrencyRate = (coreCurrencies, currencyCode = "GBP") => {

    // Read the currency metadata
    const currencyMeta = getCurrencyMeta(coreCurrencies, currencyCode);

    // Convert the exchange rate into a safe number
    const exchangeRate = toNumber(currencyMeta?.exchangerate);

    // Return one when the rate is missing
    return exchangeRate > 0 ? exchangeRate : 1;

};



// This converts a GBP value into another currency
const convertFromGbp = ({
    value,
    currencyCode = "GBP",
    coreCurrencies,
}) => {

    // Convert the GBP value into a safe number
    const safeValue = toNumber(value);

    // Read the target exchange rate
    const exchangeRate = getCurrencyRate(coreCurrencies, currencyCode);

    // Return the converted value
    return safeValue * exchangeRate;

};



// This converts another currency into GBP
const convertToGbp = ({
    value,
    currencyCode = "GBP",
    coreCurrencies,
}) => {

    // Convert the incoming value into a safe number
    const safeValue = toNumber(value);

    // Read the source exchange rate
    const exchangeRate = getCurrencyRate(coreCurrencies, currencyCode);

    // Return the converted GBP value
    return exchangeRate > 0 ? safeValue / exchangeRate : safeValue;

};



// This converts between two currencies via GBP
const convertCurrency = ({
    value,
    fromCurrencyCode = "GBP",
    toCurrencyCode = "GBP",
    coreCurrencies,
}) => {

    // Convert the source value into GBP
    const gbpValue = convertToGbp({
        value,
        currencyCode: fromCurrencyCode,
        coreCurrencies,
    });

    // Convert the GBP value into the target currency
    return convertFromGbp({
        value: gbpValue,
        currencyCode: toCurrencyCode,
        coreCurrencies,
    });

};



// This formats a number as currency
const formatMoney = ({
    value,
    currencyCode = "GBP",
    coreCurrencies,
}) => {

    // Read the currency metadata
    const currencyMeta = getCurrencyMeta(coreCurrencies, currencyCode);

    // Convert the value to a safe number
    const safeValue = toNumber(value);

    // Return the formatted currency string
    return `${currencyMeta?.symbol || "£"}${safeValue.toFixed(2)}`;

};



// This converts an ex-tax value into an inc-tax value
const convertExcTaxToIncTax = ({
    value,
    resolvedVatRate,
}) => {

    // Convert the value into a safe number
    const safeValue = toNumber(value);

    // Convert the VAT rate into a decimal
    const vatDecimalRate = toNumber(resolvedVatRate) / 100;

    // Return the value unchanged when there is no VAT rate
    if (vatDecimalRate <= 0) return safeValue;

    // Return the inc-tax value
    return safeValue * (1 + vatDecimalRate);

};



// This checks whether two price configs are effectively the same
const priceConfigsAreSame = (priceConfigA, priceConfigB) => {

    // Compare the mode values
    if ((priceConfigA?.mode || PRICE_MODES.RRP) !== (priceConfigB?.mode || PRICE_MODES.RRP)) return false;

    // Compare the currency values
    if (String(priceConfigA?.currency || "") !== String(priceConfigB?.currency || "")) return false;

    // Compare the value values as strings to avoid 1 vs "1" issues
    if (String(priceConfigA?.value ?? "") !== String(priceConfigB?.value ?? "")) return false;

    // Return true when all important values match
    return true;

};



// This resolves the store default currency from country data
export const getStoreDefaultCurrency = ({
    storeHomeCountry,
    coreCountries,
}) => {

    // Read the safe country list
    const safeCountries = Array.isArray(coreCountries)
        ? coreCountries
        : Array.isArray(coreCountries?.results)
            ? coreCountries.results
            : [];

    // Find the matching country - Return whether this country matches the store home country
    const matchedCountry = safeCountries.find((country) => (String(country?.code || "") === String(storeHomeCountry || "").toUpperCase()));

    // Get the matched default currency
    const matchedStoreDefaultCurrency = matchedCountry?.default_currency ||  "";

    // Return the matched default currency
    return matchedStoreDefaultCurrency;

};



// This gets an RRP inc-tax price for a currency from the backend prices array
const getRrpIncTaxPriceByCurrency = ({
    prices = [],
    currencyCode = "GBP",
}) => {

    // Find the usable RRP price row
    const rrpPriceRow = prices.find((priceRow) => priceRow?.blocked !== "Y") || null;

    // Return zero when there is no usable RRP price row
    if (!rrpPriceRow) return 0;

    // Normalize the currency key
    const currencyKey = String(currencyCode || "GBP").toLowerCase();

    // Return the inc-tax value for that currency
    return toNumber(rrpPriceRow?.[currencyKey]?.inc_tax);

};



// This converts a multiplier value into a user-facing adjustment percentage
const getMultiplierAdjustmentPercent = (value) => {

    // Return zero when the multiplier is blank
    if (isBlankValue(value)) return 0;

    // Convert the multiplier into a rounded adjustment percentage
    return Math.round((toNumber(value) * 100) - 100);

};



// This caps the modifier adjustment percentage before calculations
const capModifierAdjustmentPercent = ({
    percentValue,
    userTakesPayment,
}) => {

    // Convert the percentage into a safe whole number
    const safePercentValue = Math.round(toNumber(percentValue));

    // Set the minimum adjustment depending on who takes payment
    const minimumAdjustmentPercent = userTakesPayment ? -100 : 0;

    // Prevent the adjustment from going below the allowed minimum
    return Math.max(minimumAdjustmentPercent, safePercentValue);

};



// This formats an adjustment percentage for display
const formatAdjustmentPercentInput = (percentValue) => {

    // Convert the percentage into a safe whole number
    const safePercentValue = Math.round(toNumber(percentValue));

    // Return positive values with a plus sign
    if (safePercentValue > 0) return `+${safePercentValue}`;

    // Return zero and negative values normally
    return String(safePercentValue);

};



// This converts a user-facing adjustment percentage into a multiplier value
const getAdjustmentPercentAsMultiplier = (percentValue) => {

    // Convert the adjustment percentage into a safe whole number
    const safePercentValue = Math.round(toNumber(percentValue));

    // Convert the adjustment into a multiplier
    const multiplierValue = (safePercentValue + 100) / 100;

    // Return the multiplier version
    return Number(multiplierValue).toFixed(4);

};



// This gets the active tax rate from a prices array
const getTaxRateFromPrices = (prices = []) => {

    // Find the first usable price row
    const priceRow = getActiveProductPriceRow(prices);

    // Read the tax rate as a decimal, for example 0.2
    const taxRateDecimal = toNumber(priceRow?.tax_rate);

    // Convert it to a percentage, for example 20
    return taxRateDecimal * 100;

};



const findDestinationPriceProductBySku = (destinationPrices, sku, childSku) => {

    const results = Array.isArray(destinationPrices?.results)
        ? destinationPrices.results
        : Array.isArray(destinationPrices)
            ? destinationPrices
            : [];

    const targetSku = childSku || sku;

    return results.find((item) => item?.sku === targetSku) || null;

};



const getTaxRateFromDestinationPrices = ({
    destinationPrices,
    sku,
    childSku,
}) => {

    const matchedProduct = findDestinationPriceProductBySku(destinationPrices, sku, childSku);

    const priceRow = getActiveProductPriceRow(matchedProduct?.prices || []);

    const taxRateDecimal = toNumber(priceRow?.tax_rate);

    return taxRateDecimal * 100;

};



// This resolves the API price currency key
const getApiPriceCurrencyKey = (currencyCode) => {

    // Normalize the currency code
    const safeCurrencyCode = String(currencyCode || "GBP").toLowerCase();

    // Return GBP when supported
    if (safeCurrencyCode === "gbp") return "gbp";

    // Return USD when supported
    if (safeCurrencyCode === "usd") return "usd";

    // Return EUR when supported
    if (safeCurrencyCode === "eur") return "eur";

    // Return GBP as the safe fallback
    return "gbp";

};



// This gets the first active product override price row
const getActiveProductPriceRow = (prices = []) => {

    // Return null when prices is not an array
    if (!Array.isArray(prices)) return null;

    // Return the first unblocked product price row
    return prices.find((priceRow) => priceRow?.blocked !== "Y") || null;

};



// This finds the first currency with a non-blank price value
const getFirstPopulatedPriceCurrency = (priceRow) => {

    // Store the supported API currency keys
    const currencyKeys = ["gbp", "usd", "eur"];

    // Find the first currency with a real value
    const matchedCurrencyKey = currencyKeys.find((currencyKey) => {

        // Return whether this currency has a raw ex-tax value
        if (!isBlankValue(priceRow?.[currencyKey]?.raw_exc_tax)) return true;

        // Return whether this currency has an inc-tax fallback value
        if (!isBlankValue(priceRow?.[currencyKey]?.inc_tax)) return true;

        // Return false by default
        return false;

    });

    // Return null when no currency is populated
    if (!matchedCurrencyKey) return null;

    // Read the currency data
    const currencyData = priceRow?.[matchedCurrencyKey] || {};

    // Return the matched currency and value
    return {
        currencyCode: matchedCurrencyKey.toUpperCase(),
        rawExcTax: currencyData?.raw_exc_tax,
        incTax: currencyData?.inc_tax,
    };

};



// This gets the editor value from a saved API price row
const getEditorValueFromSavedPriceCurrency = ({
    mode,
    populatedCurrency,
    resolvedVatRate,
}) => {

    // Return null when there is no populated currency
    if (!populatedCurrency) return null;

    // For baseline, API returns raw ex-tax, but the user entered inc-tax
    if (mode === PRICE_MODES.BASELINE) {

        // Use raw ex-tax when available
        if (!isBlankValue(populatedCurrency?.rawExcTax)) {

            // Convert raw ex-tax back into inc-tax
            const incTaxValue = convertExcTaxToIncTax({
                value: populatedCurrency.rawExcTax,
                resolvedVatRate,
            });

            // Return the user-facing inc-tax price rounded to two decimals
            return Number(incTaxValue).toFixed(2);

        }

        // Fall back to inc_tax if the API ever sends it
        if (!isBlankValue(populatedCurrency?.incTax)) {

            // Return the inc-tax value rounded to two decimals
            return Number(toNumber(populatedCurrency.incTax)).toFixed(2);

        }

        // Return null when no value exists
        return null;

    }

    // For modifier mode, raw_exc_tax is the exact multiplier value
    if (mode === PRICE_MODES.MODIFIER) {

        // Prefer raw ex-tax because the API stores the exact multiplier there
        if (!isBlankValue(populatedCurrency?.rawExcTax)) {
            return populatedCurrency.rawExcTax;
        }

        // Fall back to inc_tax if needed
        if (!isBlankValue(populatedCurrency?.incTax)) {
            return populatedCurrency.incTax;
        }

        // Return null when no value exists
        return null;

    }

    // For profit mode, always show a money value to two decimal places
    if (mode === PRICE_MODES.PROFIT) {

        // Prefer raw ex-tax because the API stores the exact profit target there
        if (!isBlankValue(populatedCurrency?.rawExcTax)) {
            return Number(toNumber(populatedCurrency.rawExcTax)).toFixed(2);
        }

        // Fall back to inc_tax if needed
        if (!isBlankValue(populatedCurrency?.incTax)) {
            return Number(toNumber(populatedCurrency.incTax)).toFixed(2);
        }

        // Return null when no value exists
        return null;

    }

    // Return null by default
    return null;

};



// This converts a saved product prices array into the editor's local price config
export const getPriceConfigFromProductPrices = ({
    productPrices = [],
    fallbackPriceConfig,
    resolvedVatRate,
}) => {

    // Find the active product price row
    const priceRow = getActiveProductPriceRow(productPrices);

    // Return the fallback when there is no saved product price
    if (!priceRow) return fallbackPriceConfig;

    // Read the saved price mode
    const mode = priceRow?.mode || PRICE_MODES.RRP;

    // Handle baseline, modifier, and profit modes
    if (
        mode === PRICE_MODES.BASELINE ||
        mode === PRICE_MODES.MODIFIER ||
        mode === PRICE_MODES.PROFIT
    ) {

        // Find the populated currency
        const populatedCurrency = getFirstPopulatedPriceCurrency(priceRow);

        // Return fallback if no populated currency is found
        if (!populatedCurrency) return fallbackPriceConfig;

        // Resolve the editor value from the API response
        const editorValue = getEditorValueFromSavedPriceCurrency({
            mode,
            populatedCurrency,
            resolvedVatRate,
        });

        // Return fallback when no editor value exists
        if (isBlankValue(editorValue)) return fallbackPriceConfig;

        // Return local editor config
        return {
            mode,
            currency: populatedCurrency.currencyCode,
            value: editorValue,
        };

    }

    // Return RRP config for default mode
    return {
        mode: PRICE_MODES.RRP,
        value: null,
    };

};



// This builds the prices array sent back to the parent/backend
const buildPricePayload = ({
    priceConfig,
    storeCurrencyCode,
}) => {

    // Read the selected mode
    const mode = priceConfig?.mode || PRICE_MODES.RRP;

    // Resolve the API-supported store currency key
    const safeStoreCurrencyCode = getApiPriceCurrencyKey(storeCurrencyCode);

    // Return the RRP/default prices array
    if (mode === PRICE_MODES.RRP) {

        // Return an empty price config so the backend can use the default RRP
        return [];

    }

    // Return the baseline mode prices array
    if (mode === PRICE_MODES.BASELINE) {

        // Return an empty payload if there is no value
        if (isBlankValue(priceConfig?.value)) return [];

        // Build the baseline price row
        const baselinePriceRow = {

            // This stores the baseline price mode
            mode: PRICE_MODES.BASELINE,

            // This marks the row as active
            blocked: "N",

            // This stores only the populated store currency
            [safeStoreCurrencyCode]: {
                inc_tax: toNumber(priceConfig?.value),
            },

        };

        // Return the prices array
        return [
            baselinePriceRow,
        ];

    }

    // Return the modifier mode prices array
    if (mode === PRICE_MODES.MODIFIER) {

        // Return an empty payload if there is no value
        if (isBlankValue(priceConfig?.value)) return [];

        // Read the multiplier value
        const multiplierValue = toNumber(priceConfig?.value);

        // Return the prices array
        return [
            {
                // This sets the default scope
                scope: "D",

                // This sets the default country/code value
                code: "",

                // This stores the modifier price mode
                mode: PRICE_MODES.MODIFIER,

                // This marks the row as active
                blocked: "N",

                // This stores only the populated store currency
                [safeStoreCurrencyCode]: {
                    inc_tax: multiplierValue,
                },
              
            },
        ];

    }

    // Return the profit target mode prices array
    if (mode === PRICE_MODES.PROFIT) {

        // Return an empty payload if there is no value
        if (isBlankValue(priceConfig?.value)) return [];

        // Build the profit target price row
        const profitPriceRow = {

            // This stores the profit target price mode
            mode: PRICE_MODES.PROFIT,

            // This marks the row as active
            blocked: "N",

            // This stores only the populated store currency
            [safeStoreCurrencyCode]: {
                inc_tax: toNumber(priceConfig?.value),
            },

        };

        // Return the prices array
        return [
            profitPriceRow,
        ];

    }

    // Return an empty fallback so the backend uses default RRP
    return [];

};



// This returns the helper note for each price mode
const getPriceModeNote = (mode) => {

    // Return the RRP mode note
    if (mode === PRICE_MODES.RRP) {
        return "Use our 'recommended retail price'. This is automatically set in all currencies and updates whenever fulfilment costs change.";
    }

    // Return the baseline mode note
    if (mode === PRICE_MODES.BASELINE) {
        return "Set a fixed store price. This will not change if costs or RRP update.";
    }

    // Return the modifier mode note
    if (mode === PRICE_MODES.MODIFIER) {
        return "Adjust the RRP by a percentage. Prices update automatically in all currencies when the RRP changes.";
    }

    // Return the profit mode note
    if (mode === PRICE_MODES.PROFIT) {
        return "Set your minimum product profit. The product price is calculated from the active subscription level, so higher subscription levels can charge less while preserving the target profit.";
    }

    // Return fallback note
    return "";

};



// This resolves the effective sale price for calculations/display
const getResolvedSalePrice = ({
    priceConfig,
    rrpPrices,
    storeCurrencyCode,
    displayCurrencyCode,
    coreCurrencies,
    product,
    currentPlanLevel,
    selectedPrintAreas,
    subscriptionLevels,
    userTakesPayment,
    resolvedVatRate,
    destination,
    accountDefaultCurrency,
    serviceCodes,
    selectedShippingCountry,
    selectedShippingMethod,
    selectedShippingPrice,
}) => {

    // Read the selected mode
    const mode = priceConfig?.mode || PRICE_MODES.RRP;

    // Read the backend RRP in the active display currency
    const rrpDisplayPrice = getRrpIncTaxPriceByCurrency({
        prices: rrpPrices,
        currencyCode: displayCurrencyCode,
    });

    // Handle RRP mode
    if (mode === PRICE_MODES.RRP) {

        // Return the backend RRP directly
        return rrpDisplayPrice;

    }

    // Handle baseline mode
    if (mode === PRICE_MODES.BASELINE) {

        // Return the RRP when baseline is blank
        if (isBlankValue(priceConfig?.value)) {

            // Return the backend RRP directly
            return rrpDisplayPrice;

        }

        // Convert baseline from store currency into display currency
        return convertCurrency({
            value: priceConfig?.value,
            fromCurrencyCode: storeCurrencyCode,
            toCurrencyCode: displayCurrencyCode,
            coreCurrencies,
        });

    }

    // Handle modifier mode
    if (mode === PRICE_MODES.MODIFIER) {

        // Read the multiplier
        const multiplier = isBlankValue(priceConfig?.value)
            ? 1
            : toNumber(priceConfig?.value);

        // Return the RRP multiplied by the modifier
        return rrpDisplayPrice * multiplier;

    }

    // Handle profit target mode
    if (mode === PRICE_MODES.PROFIT) {

        // Return the RRP when the profit target is blank
        if (isBlankValue(priceConfig?.value)) {

            // Return the backend RRP directly
            return rrpDisplayPrice;

        }

        // Return the RRP when the product is missing
        if (!product) {

            // Return the backend RRP directly
            return rrpDisplayPrice;

        }

        // Return the calculated sale price needed for the target profit
        return getSalePriceForTargetProfit({
            product,
            level: currentPlanLevel,
            targetProfit: priceConfig?.value,
            selectedPrintAreas,
            subscriptionLevels,
            userTakesPayment,
            resolvedVatRate,
            destination,
            displayCurrencyCode,
            accountDefaultCurrency,
            coreCurrencies,
            serviceCodes,
            selectedShippingCountry,
            selectedShippingMethod,
            selectedShippingPrice,
        });

    }

    // Return the backend RRP as fallback
    return rrpDisplayPrice;

};



// This finds a pricing row by subscription level
const findPriceRowByLevel = (rows = [], level) => {

    // Return the first matching row or null
    return rows.find((row) => Number(row?.level) === Number(level)) || null;

};



// This gets the inc-tax price from a rate row
const getRateRowPrice = (row) => {

    // Return zero when there is no row
    if (!row) return 0;

    // Return the inc-tax price
    return toNumber(row?.price_inc_tax);

};



// This gets the exc-tax price from a rate row
const getRateRowPriceExcTax = (row) => {

    // Return zero when there is no row
    if (!row) return 0;

    // Return the exc-tax price
    return toNumber(row?.price_exc_tax);

};



// This finds the matching rate card product by sku
const findRateCardProductBySku = (rateCard, sku, childSku) => {

    // Read the rate card results safely
    const results = Array.isArray(rateCard?.results) ? rateCard.results : [];

    // Find the parent product
    const parentProduct = results.find((item) => item?.sku === sku) || null;

    // Find the child product
    const childProduct = parentProduct?.children?.find((child) => child?.sku === childSku) || null;

    // Return the child product when child sku exists
    return childSku ? childProduct : parentProduct;

};



// This safely returns the subscription level list
const getSafeSubscriptionLevels = (subscriptionLevels) => {

    // Return the array directly when already an array
    if (Array.isArray(subscriptionLevels)) return subscriptionLevels;

    // Return the results array when present
    if (Array.isArray(subscriptionLevels?.results)) return subscriptionLevels.results;

    // Return an empty list by default
    return [];

};



// This finds the full subscription level object for a level
const getSubscriptionLevelByLevel = (level, subscriptionLevels) => {

    // Read the safe subscription levels list
    const safeLevels = getSafeSubscriptionLevels(subscriptionLevels);

    // Return the matched level object or null
    return safeLevels.find((item) => Number(item?.level) === Number(level)) || null;

};



// This gets the raw plan name for a level
const getPlanNameByLevel = (level, subscriptionLevels) => {

    // Find the matched level object
    const matchedLevel = getSubscriptionLevelByLevel(level, subscriptionLevels);

    // Return the real plan name when found
    if (matchedLevel?.name) return matchedLevel.name;

    // Return a fallback name
    return `Plan ${level}`;

};



// This gets the subscription settings object for a level
const getPlanSettingsByLevel = (level, subscriptionLevels) => {

    // Find the matched level object
    const matchedLevel = getSubscriptionLevelByLevel(level, subscriptionLevels);

    // Return the settings object or an empty object
    return matchedLevel?.settings || {};

};



// This gets the platform fee rate for a level
const getStorePlatformFeeRateByLevel = (level, subscriptionLevels) => {

    // Read the settings object safely
    const settings = getPlanSettingsByLevel(level, subscriptionLevels);

    // Return the platform fee rate as a decimal
    return toNumber(settings?.store_platform_fee);

};



// This gets the payment fee rate for a level
const getStorePaymentFeeRateByLevel = (level, subscriptionLevels) => {

    // Read the settings object safely
    const settings = getPlanSettingsByLevel(level, subscriptionLevels);

    // Return the payment fee rate as a decimal
    return toNumber(settings?.store_payment_fee);

};



// This calculates the fulfilment breakdown for the product at one level in GBP
const getProductFulfilmentBreakdown = ({
    product,
    level,
    selectedPrintAreas = [],
    userTakesPayment,
    serviceCodes,
    selectedShippingCountry,
    selectedShippingMethod,
}) => {

    // Return empty values if the product does not exist
    if (!product) {

        // Return the empty breakdown
        return {
            basePrice: 0,
            printAreasCost: 0,
            fulfilmentCost: 0,
            matchedPrintAreas: [],
            shippingCost: 0,
        };

    }

    // Find the matching base price row
    const basePriceRow = findPriceRowByLevel(product?.base_price || [], level);

    // Read the inc-tax base price
    const basePrice = userTakesPayment ? getRateRowPrice(basePriceRow) : getRateRowPriceExcTax(basePriceRow);

    // Start the print area total at zero
    let printAreasCost = 0;

    // Store the matched print areas
    const matchedPrintAreas = [];

    // Loop through the selected print area roles
    selectedPrintAreas.forEach((selectedPrintAreaRole) => {

        // Find the matching print area in the rate card by role
        const matchedPrintArea = (product?.print_areas || []).find(
            (printArea) => printArea?.role === selectedPrintAreaRole
        );

        // Stop this iteration if the print area does not exist
        if (!matchedPrintArea) return;

        // Find the matching print area price row
        const matchedPrintAreaPriceRow = findPriceRowByLevel(
            matchedPrintArea?.print_prices || [],
            level
        );

        // Read the print area price
        const printAreaPrice = userTakesPayment ? getRateRowPrice(matchedPrintAreaPriceRow) : getRateRowPriceExcTax(matchedPrintAreaPriceRow);

        // Add this print area price to the running total
        printAreasCost += printAreaPrice;

        // Store the matched print area breakdown
        matchedPrintAreas.push({
            printAreaRole: matchedPrintArea?.role,
            printAreaName: matchedPrintArea?.name || matchedPrintArea?.role,
            price: printAreaPrice,
        });

    });

    // Calculate the fulfilment shipping cost
    const shippingCost = getFulfilmentShippingCost({
        serviceCodes,
        shippingCountry: selectedShippingCountry,
        shippingMethod: selectedShippingMethod,
        level,
        userTakesPayment,
    });

    // Calculate the full fulfilment cost including shipping
    const fulfilmentCost = basePrice + printAreasCost + shippingCost;

    // Return the full breakdown
    return {
        basePrice,
        printAreasCost,
        fulfilmentCost,
        matchedPrintAreas,
        shippingCost,
    };

};



// This calculates the fulfilment cost for the product at one level in GBP
const getProductProductionCost = ({
    product,
    level,
    selectedPrintAreas = [],
    userTakesPayment
}) => {

    // Read the fulfilment breakdown
    const fulfilmentBreakdown = getProductFulfilmentBreakdown({
        product,
        level,
        selectedPrintAreas,
        userTakesPayment,
    });

    // Return the fulfilment cost
    return fulfilmentBreakdown.fulfilmentCost;

};



// This calculates the fee breakdown for a subscription level
const getFeeBreakdown = ({
    salePrice,
    level,
    subscriptionLevels,
    userTakesPayment,
}) => {

    // Convert the sale price into a safe number
    const safeSalePrice = toNumber(salePrice);

    // Read the platform fee rate for this level
    const platformFeeRate = getStorePlatformFeeRateByLevel(level, subscriptionLevels);

    // Read the payment fee rate for this level
    const paymentFeeRate = getStorePaymentFeeRateByLevel(level, subscriptionLevels);


    /* Calculate the rounded platform fee  
    const platformFee = userTakesPayment ?
    
        // Add VAT (20%) to the platform fee if the user is taking payment because we will charge them VAT
        roundUpToNearestPenny((safeSalePrice * platformFeeRate) + ((safeSalePrice * platformFeeRate) * 0.2))
        
        :
        
        roundUpToNearestPenny(safeSalePrice * platformFeeRate);
    */

    // Calculate the rounded platform fee
    const platformFee = roundUpToNearestPenny(safeSalePrice * platformFeeRate);


    // Calculate the rounded payment fee unless the user takes payment
    const paymentFee = userTakesPayment
        ? 0
        : roundUpToNearestPenny(safeSalePrice * paymentFeeRate);

    // Calculate the total fees
    const fees = platformFee + paymentFee;

    // Return the full fee breakdown
    return {
        platformFeeRate,
        paymentFeeRate,
        platformFee,
        paymentFee,
        fees,
    };

};



// This safely returns the shipping destination list
const getSafeShippingDestinations = (shippingDestinations) => {

    // Return the array directly when already an array
    if (Array.isArray(shippingDestinations)) return shippingDestinations;

    // Return an empty array by default
    return [];

};



// This finds a destination object by name
const findShippingDestination = (shippingDestinations, selectedDestinationValue) => {

    // Read the safe destination list
    const safeDestinations = getSafeShippingDestinations(shippingDestinations);

    // Return the first matching destination
    return safeDestinations.find((destination) => {

        // Return whether this destination matches
        return destination?.name === selectedDestinationValue;

    }) || null;

};



// This checks whether tax should be applied for the selected destination
const getDestinationIncludesTax = (destination) => {

    // Return true only when the destination explicitly collects tax
    return Boolean(destination?.collect_tax === "Y");

};



// This calculates the tax amount included within the entered sale price
const getTaxAmount = ({
    salePrice,
    resolvedVatRate,
    destination,
    userTakesPayment,
}) => {

    // Return zero when the user handles payment
    if (userTakesPayment) return 0;

    // Return zero when the destination does not collect tax
    if (!getDestinationIncludesTax(destination)) return 0;

    // Convert the sale price into a safe number
    const safeSalePrice = toNumber(salePrice);

    // Convert the VAT percentage into a decimal
    const vatDecimalRate = toNumber(resolvedVatRate) / 100;

    // Return zero when there is no VAT rate
    if (vatDecimalRate <= 0) return 0;

    // Calculate the VAT exclusive portion
    const priceExcTax = safeSalePrice / (1 + vatDecimalRate);

    // Calculate the tax amount included
    const taxAmount = safeSalePrice - priceExcTax;

    // Return the rounded tax amount
    return roundToNearestPenny(taxAmount);

};



// This calculates the entered sale price excluding tax
const getPriceExcludingTax = ({
    salePrice,
    resolvedVatRate,
    destination,
    userTakesPayment,
}) => {

    // Convert the sale price into a safe number
    const safeSalePrice = toNumber(salePrice);

    // Return the full sale price when the user handles payment
    if (userTakesPayment) return safeSalePrice;

    // Return the full sale price when the destination does not collect tax
    if (!getDestinationIncludesTax(destination)) return safeSalePrice;

    // Convert the VAT percentage into a decimal
    const vatDecimalRate = toNumber(resolvedVatRate) / 100;

    // Return the full sale price when there is no VAT rate
    if (vatDecimalRate <= 0) return safeSalePrice;

    // Return the VAT exclusive price
    return safeSalePrice / (1 + vatDecimalRate);

};



// This returns the canonical inc-tax store price for one currency



// This calculates one full sale outcome for a level for displaying values in the breakdown table
const getSaleOutcome = ({
    product,
    level,
    salePrice,
    selectedPrintAreas,
    subscriptionLevels,
    userTakesPayment,
    resolvedVatRate,
    destination,
    displayCurrencyCode,
    accountDefaultCurrency,
    coreCurrencies,
    serviceCodes,
    selectedShippingCountry,
    selectedShippingMethod,
    shippingPrice,
}) => {

    // Calculate the fulfilment breakdown in GBP
    const fulfilmentBreakdown = getProductFulfilmentBreakdown({
        product,
        level,
        selectedPrintAreas,
        userTakesPayment,
        serviceCodes,
        selectedShippingCountry,
        selectedShippingMethod,
    });

    // Convert the fulfilment base price from GBP into account currency
    const basePriceInAccountCurrency = convertCurrency({
        value: fulfilmentBreakdown.basePrice,
        fromCurrencyCode: "GBP",
        toCurrencyCode: accountDefaultCurrency,
        coreCurrencies,
    });

    // Convert the print areas cost from GBP into account currency
    const printAreasCostInAccountCurrency = convertCurrency({
        value: fulfilmentBreakdown.printAreasCost,
        fromCurrencyCode: "GBP",
        toCurrencyCode: accountDefaultCurrency,
        coreCurrencies,
    });

    // Convert the fulfilment cost from GBP into account currency
    const productionCostInAccountCurrency = convertCurrency({
        value: fulfilmentBreakdown.fulfilmentCost,
        fromCurrencyCode: "GBP",
        toCurrencyCode: accountDefaultCurrency,
        coreCurrencies,
    });

    // Convert the matched print areas from GBP into display currency
    const matchedPrintAreas = fulfilmentBreakdown.matchedPrintAreas.map((printArea) => {

        // Return the converted print area
        return {
            ...printArea,
            price: printArea?.price,
        };

    });

    // Convert the sale price into a safe number
    const safeSalePrice = toNumber(salePrice);

    // Convert shipping price into a safe number
    const safeShippingPrice = toNumber(shippingPrice);

    // Calculate the full customer order total
    const orderTotal = safeSalePrice + safeShippingPrice;

    // Calculate the fee breakdown
    const feeBreakdown = getFeeBreakdown({
        salePrice: orderTotal,
        level,
        subscriptionLevels,
        userTakesPayment,
    });

    // Calculate the price excluding tax
    const priceExcTax = getPriceExcludingTax({
        salePrice: orderTotal,
        resolvedVatRate,
        destination,
        userTakesPayment,
    });

    // Calculate the tax amount included in the sale price
    const tax = getTaxAmount({
        salePrice: orderTotal,
        resolvedVatRate,
        destination,
        userTakesPayment,
    });

    // Calculate the total cost in the store currency
    const totalFeesCostInStoreCurrency = feeBreakdown.platformFee + feeBreakdown.paymentFee + tax;

    // Get order total minus fees and tax in store currency
    const netRevenueInStoreCurrency = orderTotal - totalFeesCostInStoreCurrency;

    // Convert to account currency
    const storePriceMinusFeesInAccountCurrency = convertCurrency({
        value: netRevenueInStoreCurrency,
        fromCurrencyCode: displayCurrencyCode,
        toCurrencyCode: accountDefaultCurrency,
        coreCurrencies,
    });

    // Calculate the net value before fulfilment
    const netBeforeFulfilment = priceExcTax - feeBreakdown.fees;

    // Net value before fulfilment in account currency
    const netBeforeFulfilmentInAccountCurrency = convertCurrency({
        value: netBeforeFulfilment,
        fromCurrencyCode: displayCurrencyCode,
        toCurrencyCode: accountDefaultCurrency,
        coreCurrencies,
    });

    // Calculate the 'you mke' profit in the account currency
    const profitInAccountCurrency = netBeforeFulfilmentInAccountCurrency - productionCostInAccountCurrency;

    // Get the exchange rate to display in the table
    const exchangeRate = Number( (getCurrencyRate(coreCurrencies, accountDefaultCurrency) / getCurrencyRate(coreCurrencies, displayCurrencyCode)).toFixed(4) );

    // Convert fulfilment shipping from GBP into account currency
    const shippingCostInAccountCurrency = convertCurrency({
        value: fulfilmentBreakdown.shippingCost,
        fromCurrencyCode: "GBP",
        toCurrencyCode: accountDefaultCurrency,
        coreCurrencies,
    });

    // Return the outcome object
    return {
        salePrice,

        basePrice: basePriceInAccountCurrency,
        printAreasCost: printAreasCostInAccountCurrency,
        productionCost: productionCostInAccountCurrency,

        matchedPrintAreas,

        totalFeesCostInStoreCurrency,
        tax,
        priceExcTax,
        platformFeeRate: feeBreakdown.platformFeeRate,
        paymentFeeRate: feeBreakdown.paymentFeeRate,
        platformFee: feeBreakdown.platformFee,
        paymentFee: feeBreakdown.paymentFee,
        fees: feeBreakdown.fees,
        profitInAccountCurrency,
        netRevenueInStoreCurrency,
        storePriceMinusFeesInAccountCurrency,
        exchangeRate,

        shippingPrice: safeShippingPrice,
        orderTotal,
        shippingCost: shippingCostInAccountCurrency,
    };

};



// This finds the minimum valid sale price for a level in the display currency
const getMinimumSalePrice = ({
    product,
    level,
    selectedPrintAreas,
    subscriptionLevels,
    userTakesPayment,
    resolvedVatRate,
    destination,
    displayCurrencyCode,
    accountDefaultCurrency,
    coreCurrencies,
    serviceCodes,
    selectedShippingCountry,
    selectedShippingMethod,
    selectedShippingPrice,
}) => {

    // Calculate the fulfilment cost in the account currency
    const productionCost = getProductProductionCost({
        product,
        level,
        selectedPrintAreas,
        userTakesPayment,
    });

    // Convert the fulfilment cost into display currency as the starting point
    const productionCostInDisplayCurrency = convertCurrency({
        value: productionCost,
        fromCurrencyCode: accountDefaultCurrency,
        toCurrencyCode: displayCurrencyCode,
        coreCurrencies,
    });

    // Start from the converted fulfilment cost rounded up to the nearest penny
    let candidatePrice = roundUpToNearestPenny(productionCostInDisplayCurrency);

    // Add a safety counter so the loop cannot run forever
    let safetyCount = 0;

    // Keep increasing until the candidate price breaks even in account currency
    while (safetyCount < 100000) {

        // Calculate the sale outcome for this candidate price
        const outcome = getSaleOutcome({
            product,
            level,
            salePrice: candidatePrice,
            selectedPrintAreas,
            subscriptionLevels,
            userTakesPayment,
            resolvedVatRate,
            destination,
            displayCurrencyCode,
            accountDefaultCurrency,
            coreCurrencies,
            serviceCodes,
            selectedShippingCountry,
            selectedShippingMethod,
            shippingPrice: selectedShippingPrice,
        });

        // Return this price when account currency profit is zero or higher
        if (roundToNearestPenny(outcome.profitInAccountCurrency) >= 0) return candidatePrice;

        // Move up by one penny in the store/display currency
        candidatePrice = Number((candidatePrice + 0.01).toFixed(2));

        // Increment the safety counter
        safetyCount += 1;

    }

    // Return the last candidate as a safe fallback
    return candidatePrice;

};


// This finds the sale price needed to make a target profit in the user's account currency
const getSalePriceForTargetProfit = ({
    product,
    level,
    targetProfit,
    selectedPrintAreas,
    subscriptionLevels,
    userTakesPayment,
    resolvedVatRate,
    destination,
    displayCurrencyCode,
    accountDefaultCurrency,
    coreCurrencies,
    serviceCodes,
    selectedShippingCountry,
    selectedShippingMethod,
    selectedShippingPrice,

}) => {

    // Convert the target profit into a safe account currency number
    const targetProfitInAccountCurrency = toNumber(targetProfit);

    // Calculate the minimum break-even price first
    let candidatePrice = getMinimumSalePrice({
        product,
        level,
        selectedPrintAreas,
        subscriptionLevels,
        userTakesPayment,
        resolvedVatRate,
        destination,
        displayCurrencyCode,
        accountDefaultCurrency,
        coreCurrencies,
        serviceCodes,
        selectedShippingCountry,
        selectedShippingMethod,
        selectedShippingPrice,
    });

    // Add a safety counter so the loop cannot run forever
    let safetyCount = 0;

    // Keep increasing until the account currency profit target is reached
    while (safetyCount < 100000) {

        // Calculate the sale outcome for this candidate price
        const outcome = getSaleOutcome({
            product,
            level,
            salePrice: candidatePrice,
            selectedPrintAreas,
            subscriptionLevels,
            userTakesPayment,
            resolvedVatRate,
            destination,
            displayCurrencyCode,
            accountDefaultCurrency,
            coreCurrencies,
            serviceCodes,
            selectedShippingCountry,
            selectedShippingMethod,
            shippingPrice: selectedShippingPrice,
        });

        // Round the calculated profit to normal money precision
        const roundedProfitInAccountCurrency = roundToNearestPenny(outcome.profitInAccountCurrency);

        // Return this price when it reaches the account currency target profit
        if (roundedProfitInAccountCurrency >= targetProfitInAccountCurrency) return candidatePrice;

        // Move up by one penny in the store/display currency
        candidatePrice = Number((candidatePrice + 0.01).toFixed(2));

        // Increment the safety counter
        safetyCount += 1;

    }

    // Return the last candidate as a safe fallback
    return candidatePrice;

};



// This safely returns active shipping methods from the selected store connection
const getActiveShippingMethods = (selectedStoreConnection) => {

    // Read the shipping methods safely
    const shippingMethods = Array.isArray(selectedStoreConnection?.shipping_methods) ? selectedStoreConnection.shipping_methods : [];

    // Return only active shipping methods
    return shippingMethods.filter((shippingMethod) => shippingMethod?.active === "Y");

};



// This gets the available shipping countries from active shipping methods
export const getAvailableShippingCountries = (selectedStoreConnection) => {

    // Read the active shipping methods
    const activeShippingMethods = getActiveShippingMethods(selectedStoreConnection);

    // Create a unique country set
    const countrySet = new Set();

    // Loop through each active shipping method
    activeShippingMethods.forEach((shippingMethod) => {

        // Read countries safely
        const countries = Array.isArray(shippingMethod?.countries) ? shippingMethod.countries : [];

        // Add each country to the set
        countries.forEach((country) => countrySet.add(country));

    });

    // Return countries alphabetically
    return Array.from(countrySet).sort();

};



// This gets shipping methods available for one country
export const getShippingMethodsForCountry = ({
    selectedStoreConnection,
    selectedShippingCountry,
}) => {

    // Read the active shipping methods
    const activeShippingMethods = getActiveShippingMethods(selectedStoreConnection);

    // Return methods that include the selected country
    return activeShippingMethods.filter((shippingMethod) => {

        // Read countries safely
        const countries = Array.isArray(shippingMethod?.countries) ? shippingMethod.countries : [];

        // Return whether this method supports the selected country
        return countries.includes(selectedShippingCountry);

    });

};



// This gets the customer-facing shipping price for the selected currency
const getShippingPriceForCurrency = ({
    shippingMethod,
    currencyCode,
}) => {

    // Read the currency pricing safely
    const currencyPricing = shippingMethod?.pricing?.[currencyCode] || {};

    // Return the inc-tax shipping price
    return toNumber(currencyPricing?.price_inc_tax);

};



// This finds the fulfilment shipping service by shipping method code
const findServiceCodeByShippingMethod = ({
    serviceCodes,
    shippingCountry,
    shippingMethod,
}) => {

    // Read service codes safely
    const safeServiceCodes = Array.isArray(serviceCodes) ? serviceCodes : [];

    // Return the matching service code
    return safeServiceCodes.find((country) => country.country === shippingCountry)?.services?.find((serviceCode) => serviceCode?.service_code === shippingMethod?.code) || null;

};



// This gets the fulfilment shipping cost from a matched service code
const getFulfilmentShippingCost = ({
    serviceCodes,
    shippingCountry,
    shippingMethod,
    level,
    userTakesPayment,
}) => {

    // Find the matching service code
    const matchedServiceCode = findServiceCodeByShippingMethod({
        serviceCodes,
        shippingCountry,
        shippingMethod,
    });
    
    // Return inc-tax when user takes payment - taken out MVP
    //if (userTakesPayment) return getRateRowPrice(matchedServiceCode);

    // Return ex-tax when platform takes payment
    return getRateRowPriceExcTax(matchedServiceCode);

};



// This renders the shared summary metrics and price controls
const ProductPriceEditorSummaryMetrics = ({
    priceConfig,
    setPriceConfig,
    displaySalePrice,
    displayCost,
    displayProfit,
    displayCurrencyCode,
    displayValuesStatus = "unavailable",
    profitCurrencyCode,
    storeCurrencyCode,
    coreCurrencies,
    minimumBaselinePrice,
    priceWarning,
    setPriceWarning,
    onCommitPriceConfig,
    setShowPriceEditorPopUp,
    isFullEditor = false,
    userTakesPayment,
    displayCostCard = true,
    disablePriceEditor = false,
}) => {


    // Read the active mode
    const activeMode = priceConfig?.mode || PRICE_MODES.RRP;

    // Read whether the baseline has a locked value
    const baselineIsLocked = activeMode === PRICE_MODES.BASELINE && !isBlankValue(priceConfig?.value);

    // Read whether API display values are ready
    const displayValuesAreReady = displayValuesStatus === "ready";

    // Read whether API display values failed
    const displayValuesAreUnavailable = displayValuesStatus === "error" || displayValuesStatus === "unavailable";

    // Render a display money value without falling back to local calculations
    const renderDisplayMoney = ({
        value,
        currencyCode,
    }) => {

        // Return the API value when available
        if (displayValuesAreReady && !isBlankValue(value)) {

            return formatMoney({
                value,
                currencyCode,
                coreCurrencies,
            });

        }

        // Return unavailable when the mock order could not be calculated
        if (displayValuesAreUnavailable) {

            return "Unavailable";

        }

        // Return unavailable when the mock order completed but this specific value was not returned
        if (displayValuesAreReady) {

            return "Unavailable";

        }

        // Return calculating while the mock order is loading
        return "Calculating...";

    };

    // Read whether the API profit value is negative
    const displayProfitIsNegative = displayValuesAreReady && !isBlankValue(displayProfit) && toNumber(displayProfit) < 0;

    // Store the visible modifier percentage input
    const [modifierPercentInput, setModifierPercentInput] = useState("0");


    // Keep the visible modifier input synced with the saved modifier value
    useEffect(() => {

        // Stop when the active mode is not modifier
        if (activeMode !== PRICE_MODES.MODIFIER) return;

        // Convert the saved multiplier into a user-facing adjustment percentage
        const nextModifierPercent = getMultiplierAdjustmentPercent(priceConfig?.value);

        // Store the adjustment percentage as visible input text
        setModifierPercentInput(formatAdjustmentPercentInput(nextModifierPercent));

    }, [
        activeMode,
        priceConfig?.value,
    ]);


    // Re-cap modifier values when the payment handler changes
    useEffect(() => {

        // Stop when the active mode is not modifier
        if (activeMode !== PRICE_MODES.MODIFIER) return;

        // Stop when the current value is blank
        if (isBlankValue(priceConfig?.value)) return;

        // Convert the saved multiplier into an adjustment percentage
        const currentAdjustmentPercent = getMultiplierAdjustmentPercent(priceConfig?.value);

        // Cap the adjustment based on who takes payment
        const cappedAdjustmentPercent = capModifierAdjustmentPercent({
            percentValue: currentAdjustmentPercent,
            userTakesPayment,
        });

        // Stop if the current adjustment is already valid
        if (Number(currentAdjustmentPercent) === Number(cappedAdjustmentPercent)) return;

        // Build the next price config
        const nextPriceConfig = {
            mode: PRICE_MODES.MODIFIER,
            value: getAdjustmentPercentAsMultiplier(cappedAdjustmentPercent),
        };

        // Update the visible input
        setModifierPercentInput(formatAdjustmentPercentInput(cappedAdjustmentPercent));

        // Update local state
        setPriceConfig(nextPriceConfig);

        // Notify parent
        onCommitPriceConfig(nextPriceConfig);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ userTakesPayment]);



    // Handle switching to RRP
    const handleUseRrp = () => {

        // Build the next price config
        const nextPriceConfig = {
            mode: PRICE_MODES.RRP,
            value: null,
        };

        // Update local state
        setPriceConfig(nextPriceConfig);

        // Clear warning
        setPriceWarning("");

        // Notify parent
        onCommitPriceConfig(nextPriceConfig);

    };



    // Handle switching to baseline mode
    const handleUseBaseline = () => {

        // Build the next price config
        const nextPriceConfig = {
            mode: PRICE_MODES.BASELINE,
            currency: storeCurrencyCode,
            value: activeMode === PRICE_MODES.BASELINE ? priceConfig?.value : "",
        };

        // Update local state
        setPriceConfig(nextPriceConfig);

        // Clear warning
        setPriceWarning("");

    };



    // Handle switching to modifier mode
    const handleUseModifier = () => {

        // Read whether the existing modifier value is blank
        const modifierValueIsBlank = isBlankValue(priceConfig?.value);

        // Build the next visible adjustment percentage value
        const nextModifierPercentInput = modifierValueIsBlank
            ? "0"
            : formatAdjustmentPercentInput(getMultiplierAdjustmentPercent(priceConfig?.value));

        // Build the next price config
        const nextPriceConfig = {
            mode: PRICE_MODES.MODIFIER,
            value: activeMode === PRICE_MODES.MODIFIER ? priceConfig?.value : null,
        };

        // Update the visible modifier input
        setModifierPercentInput(nextModifierPercentInput);

        // Update local state
        setPriceConfig(nextPriceConfig);

        // Clear warning
        setPriceWarning("");

        // Notify parent
        onCommitPriceConfig(nextPriceConfig);

    };



    // Handle baseline input changes
    const handleBaselineChange = (event) => {

        // Clean the typed value
        const nextValue = cleanMoneyInput(event.target.value);

        // Build the next config
        const nextPriceConfig = {
            mode: PRICE_MODES.BASELINE,
            currency: storeCurrencyCode,
            value: nextValue,
        };

        // Update local state
        setPriceConfig(nextPriceConfig);

        // Clear warning when blank
        if (isBlankValue(nextValue)) {

            // Clear warning
            setPriceWarning("");

            // Stop here
            return;

        }
        
        // Show warning if below minimum and the platform is taking payment
        if ((Number(nextValue) < Number(minimumBaselinePrice))) {

            if (!userTakesPayment) {

                // Set warning
                setPriceWarning(`Minimum price must be ${formatMoney({
                    value: minimumBaselinePrice,
                    currencyCode: storeCurrencyCode,
                    coreCurrencies,
                })}`);

            } else {

                // Set warning
                setPriceWarning(`Minimum price for profit ${formatMoney({
                    value: minimumBaselinePrice,
                    currencyCode: storeCurrencyCode,
                    coreCurrencies,
                })}`);

            }

        } else {

            // Clear warning
            setPriceWarning("");

        }

    };



    // Handle baseline blur
    const handleBaselineBlur = () => {

        // Read the typed value
        const typedValue = priceConfig?.value;

        
        // If blank then return to RRP
        if (isBlankValue(typedValue)) {

            // Use RRP
            //handleUseRrp();

            // Stop here
            return;

        }
        
        let finalValue;

        // Check if the platform takes payment
        if (!userTakesPayment) {

            // Clamp value to minimum
            finalValue = Math.max(Number(typedValue), Number(minimumBaselinePrice)).toFixed(2);

        } else {

            finalValue = typedValue

        }


        // Build the next config
        const nextPriceConfig = {
            mode: PRICE_MODES.BASELINE,
            currency: storeCurrencyCode,
            value: finalValue,
        };

        // Update local state
        setPriceConfig(nextPriceConfig);

        // Clear warning
        setPriceWarning("");

        // Notify parent
        onCommitPriceConfig(nextPriceConfig);

    };



    // Handle modifier percentage changes
    const handleModifierPercentChange = (event) => {

        // Clean the typed percentage adjustment value
        const nextPercentValue = cleanPercentInput(event.target.value);

        // Update the visible input immediately
        setModifierPercentInput(nextPercentValue);

        // Stop if the user is only typing a sign
        if (nextPercentValue === "-" || nextPercentValue === "+") return;

        // Stop if the input is blank
        if (isBlankValue(nextPercentValue)) {

            // Build the blank modifier config
            const nextPriceConfig = {
                mode: PRICE_MODES.MODIFIER,
                value: null,
            };

            // Update local state
            setPriceConfig(nextPriceConfig);

            // Stop here
            return;

        }

        // Cap the adjustment percentage before calculating with it
        const cappedPercentValue = capModifierAdjustmentPercent({
            percentValue: nextPercentValue,
            userTakesPayment,
        });

        // Build the next config using the capped percentage
        const nextPriceConfig = {
            mode: PRICE_MODES.MODIFIER,
            value: getAdjustmentPercentAsMultiplier(cappedPercentValue),
        };

        // Update local state
        setPriceConfig(nextPriceConfig);

    };



    // Handle modifier percentage blur
    const handleModifierPercentBlur = () => {

        // If the input is blank, show zero again but keep the backend value as null
        if (isBlankValue(modifierPercentInput) || modifierPercentInput === "-" || modifierPercentInput === "+") {

            // Build the null modifier config
            const nextPriceConfig = {
                mode: PRICE_MODES.MODIFIER,
                value: null,
            };

            // Reset the visible input to zero
            setModifierPercentInput("0");

            // Update local state
            setPriceConfig(nextPriceConfig);

            // Notify parent
            onCommitPriceConfig(nextPriceConfig);

            // Stop here
            return;

        }

        // Cap the typed adjustment percentage
        const finalPercentValue = capModifierAdjustmentPercent({
            percentValue: modifierPercentInput,
            userTakesPayment,
        });

        // Build the next config
        const nextPriceConfig = {
            mode: PRICE_MODES.MODIFIER,
            value: getAdjustmentPercentAsMultiplier(finalPercentValue),
        };

        // Update the visible input with the cleaned adjustment percentage
        setModifierPercentInput(formatAdjustmentPercentInput(finalPercentValue));

        // Update local state
        setPriceConfig(nextPriceConfig);

        // Notify parent
        onCommitPriceConfig(nextPriceConfig);

    };



    // Handle modifier step clicks
    const handleModifierStep = (stepAmount) => {

        // Read the current displayed adjustment percentage, using zero when blank
        const currentPercentValue = isBlankValue(modifierPercentInput)
            ? 0
            : toNumber(modifierPercentInput);

        // Calculate the next adjustment percent
        const nextPercentValue = capModifierAdjustmentPercent({
            percentValue: currentPercentValue + stepAmount,
            userTakesPayment,
        });

        // Build the next config
        const nextPriceConfig = {
            mode: PRICE_MODES.MODIFIER,
            value: getAdjustmentPercentAsMultiplier(nextPercentValue),
        };

        // Update the visible input value
        setModifierPercentInput(formatAdjustmentPercentInput(nextPercentValue));

        // Update local state
        setPriceConfig(nextPriceConfig);

        // Notify parent
        onCommitPriceConfig(nextPriceConfig);

    };



    // Handle modifier keyboard controls
    const handleModifierPercentKeyDown = (event) => {

        // Stop if this is not an arrow up or arrow down press
        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;

        // Prevent the cursor moving inside the input
        event.preventDefault();

        // Set the step amount from the pressed arrow key
        const stepAmount = event.key === "ArrowUp" ? 1 : -1;

        // Read the current displayed adjustment percentage, using zero when blank
        const currentPercentValue = isBlankValue(modifierPercentInput) || modifierPercentInput === "-" || modifierPercentInput === "+"
            ? 0
            : toNumber(modifierPercentInput);

        // Calculate the next adjustment percent
        const nextPercentValue = capModifierAdjustmentPercent({
            percentValue: currentPercentValue + stepAmount,
            userTakesPayment,
        });

        // Build the next config
        const nextPriceConfig = {
            mode: PRICE_MODES.MODIFIER,
            value: getAdjustmentPercentAsMultiplier(nextPercentValue),
        };

        // Update the visible input value
        setModifierPercentInput(formatAdjustmentPercentInput(nextPercentValue));

        // Update local state
        setPriceConfig(nextPriceConfig);

        // Notify parent
        onCommitPriceConfig(nextPriceConfig);

    };



    // Handle switching to profit target mode
    const handleUseProfit = () => {

        // Start with an existing minimum profit value when one is already set
        let nextProfitValue = activeMode === PRICE_MODES.PROFIT
            ? priceConfig?.value
            : "";

        // When switching from another pricing mode, use the current earnings
        // so changing pricing method does not unnecessarily change the store price
        if (
            isBlankValue(nextProfitValue) &&
            displayValuesAreReady &&
            !isBlankValue(displayProfit)
        ) {

            nextProfitValue = toNumber(displayProfit).toFixed(2);

        }

        // Fall back to a £1-equivalent minimum profit when no better value is available
        if (isBlankValue(nextProfitValue)) {

            nextProfitValue = "1.00";

        }

        // Build the next price config
        const nextPriceConfig = {
            mode: PRICE_MODES.PROFIT,
            currency: profitCurrencyCode,
            value: nextProfitValue,
        };

        // Update local state
        setPriceConfig(nextPriceConfig);

        // Clear warning
        setPriceWarning("");

        // Persist the pricing mode and value so the Advanced editor receives the same config
        onCommitPriceConfig(nextPriceConfig);

    };



    // Handle profit target input changes
    const handleProfitChange = (event) => {

        // Clean the typed value to a money-friendly format
        const nextValue = cleanMoneyInput(event.target.value);

        // Build the next config
        const nextPriceConfig = {
            mode: PRICE_MODES.PROFIT,
            currency: profitCurrencyCode,
            value: nextValue,
        };

        // Update local state
        setPriceConfig(nextPriceConfig);

        // Clear warning
        setPriceWarning("");

    };



    // Handle profit target blur
    const handleProfitBlur = () => {

        // Read the typed value
        const typedValue = priceConfig?.value;

        // Stop when blank
        if (isBlankValue(typedValue)) return;

        // Prevent negative target profit
        const finalValue = Math.max(0, Number(typedValue)).toFixed(2);

        // Build the next config
        const nextPriceConfig = {
            mode: PRICE_MODES.PROFIT,
            currency: profitCurrencyCode,
            value: finalValue,
        };

        // Update local state
        setPriceConfig(nextPriceConfig);

        // Clear warning
        setPriceWarning("");

        // Notify parent
        onCommitPriceConfig(nextPriceConfig);

    };

   

    // Return the shared metric cards
    return (

        <>

            <div className={`product-price-editor__summary-metrics product-price-editor__summary-metrics--${activeMode} product-price-editor__summary-metrics--${displayCostCard ? "" : "childRow"} ${isFullEditor ? "product-price-editor__summary-metrics--full" : ""}`} >

                <div className="product-price-editor__metric-card product-price-editor__metric-card--price">

                    <div className="product-price-editor__metric-label horizontal-align-content-center">

                        Store Price

                        <InfoButton
                            shortLabel="Store Price"
                            description="The store price is calculated from the configured product and print areas when using RRP, from the minimum profit amount and subscription level when using Min Profit, or by applying the selected percentage adjustment to the RRP when using Adjust %."
                        />

                    </div>

                    <strong className="product-price-editor__metric-value">
                        {renderDisplayMoney({
                            value: displaySalePrice,
                            currencyCode: displayCurrencyCode,
                        })}
                        {baselineIsLocked && <span>🔒</span> }
                    </strong>

                </div>

                <div className="product-price-editor__metric-card product-price-editor__metric-card--profit">

                    <div className="product-price-editor__metric-label horizontal-align-content-center">

                        You Make

                        <InfoButton
                            shortLabel="You Make"
                            description="This is based on your default store currency and destination country." 
                        />

                    </div>

                    <strong className={`product-price-editor__metric-value ${displayProfitIsNegative ? "product-price-editor__metric-value--negative-value" : ""}`}>

                        {renderDisplayMoney({
                            value: displayProfit,
                            currencyCode: profitCurrencyCode,
                        })}
                    </strong>

                </div>


                <div className="product-price-editor__summary-metrics--dropdown">

                    <br/>

                    <select 
                        value={
                            activeMode === PRICE_MODES.BASELINE
                                ? "BASELINE"
                                : activeMode === PRICE_MODES.MODIFIER
                                    ? "MODIFIER"
                                    : activeMode === PRICE_MODES.PROFIT
                                        ? "PROFIT"
                                        : "RRP"
                        }
                        disabled={disablePriceEditor}
                        onChange={(event) => {

                            // Read the selected pricing mode
                            const selectedMode = event.target.value;

                            // Use RRP mode
                            if (selectedMode === "RRP") {

                                // Switch to RRP
                                handleUseRrp();

                                // Stop here
                                return;

                            }

                            // Use fixed baseline mode
                            if (selectedMode === "BASELINE") {

                                // Switch to baseline
                                handleUseBaseline();

                                // Stop here
                                return;

                            }

                            // Use modifier mode
                            if (selectedMode === "MODIFIER") {

                                // Switch to modifier
                                handleUseModifier();

                            }

                            // Use profit target mode
                            if (selectedMode === "PROFIT") {

                                // Switch to profit target
                                handleUseProfit();

                            }

                        }}

                    >
                        <option value={"RRP"}>RRP</option>
                        <option value={"PROFIT"}>Min Profit</option>
                        {/* <option value={"BASELINE"}>Fixed</option>    **** Removed until 'user takes payment' is an option ****   */}
                        <option value={"MODIFIER"}>Adjust %</option> 
                    </select>

                    {/* 
                    {!isFullEditor && (

                        <button
                            type="button"
                            className="product-price-editor__info-button"
                            onClick={() => setShowPriceEditorPopUp(true)}
                            aria-label="Open full pricing details"
                            title="View Full Pricing Details"
                        >
                            ⓘ
                        </button>
                       
                    )}
                    */}

                    {!isFullEditor && (

                        <CreateButton 
                            title="Advanced"
                            size="xs"
                            onClick={() => setShowPriceEditorPopUp(true)}
                        />

                    )}

                </div>

                <div className="product-price-editor__metric-card product-price-editor__metric-card--control">

                    <div className="product-price-editor__quick-editor">

                        {activeMode === PRICE_MODES.BASELINE ? (

                            <label className="product-price-editor__quick-field">

                                <span className="product-price-editor__input-prefix">
                                    {getCurrencyMeta(coreCurrencies, storeCurrencyCode)?.symbol || "£"}
                                </span>

                                <input
                                    type="text"
                                    value={priceConfig?.value ?? ""}
                                    onChange={handleBaselineChange}
                                    onBlur={handleBaselineBlur}
                                    className="product-price-editor__input product-price-editor__input--with-prefix"
                                    placeholder="Use RRP"
                                    disabled={disablePriceEditor}
                                />

                            </label>

                        ) : activeMode === PRICE_MODES.MODIFIER ? (

                            <div className="product-price-editor__modifier-control">

                                <CreateButton
                                    title="-"
                                    type="button"
                                    plain={true}
                                    size="xs"
                                    onClick={() => handleModifierStep(-5)}
                                    aria-label="Reduce price modifier by 5 percent"
                                />

                                <label className="product-price-editor__modifier-field">

                                    <input
                                        type="text"
                                        value={modifierPercentInput}
                                        onChange={handleModifierPercentChange}
                                        onBlur={handleModifierPercentBlur}
                                        onKeyDown={handleModifierPercentKeyDown}
                                        className="product-price-editor__input product-price-editor__input--center"
                                        placeholder="0"
                                        disabled={disablePriceEditor}
                                    />

                                    <span className="product-price-editor__input-suffix">
                                        %
                                    </span>

                                </label>

                                <CreateButton
                                    title="+"
                                    type="button"
                                    plain={true}
                                    size="xs"
                                    onClick={() => handleModifierStep(5)}
                                    aria-label="Increase price modifier by 5 percent"
                                />

                            </div>

                        ) : activeMode === PRICE_MODES.PROFIT ? (

                            <label className="product-price-editor__modifier-field">

                                <span className="product-price-editor__input-prefix">
                                    {getCurrencyMeta(coreCurrencies, profitCurrencyCode)?.symbol || "£"}
                                </span>

                                <input
                                    type="text"
                                    value={priceConfig?.value ?? ""}
                                    onChange={handleProfitChange}
                                    onBlur={handleProfitBlur}
                                    className="product-price-editor__input product-price-editor__input--center"
                                    placeholder={displayValuesAreReady && !isBlankValue(displayProfit) ? toNumber(displayProfit).toFixed(2) : ""}
                                    disabled={disablePriceEditor}
                                />

                            </label>

                        ) : (

                            <div className="product-price-editor__mode-control-placeholder"></div>

                        )}

                        {priceWarning && (

                            <span className="product-price-editor__warning">
                                {priceWarning}
                            </span>

                        )}

                    </div>

                </div>

                
            </div>

            {isFullEditor &&

                <p className="product-price-editor__mode-note">
                    {getPriceModeNote(activeMode)}
                </p>
            
            }

        </>

    );

};

















// Export the product price editor
export const ProductPriceEditor = ({

    // This is the product sku
    sku,

    // This is the child sku if this is a child product
    childSku,

    // This is the configured store SKU used for order item lookup
    storeSku,

    // This is the user's current subscription level
    currentPlanLevel,

    // This is the subscription levels data
    subscriptionLevels = null,

    // This is the backend RRP prices array - calculated for the store's home country
    defaultPrices = [],

    // This is the product's saved override prices array
    productPrices = [],

    // This is the initial mode-based price config
    initialPriceConfig = {
        mode: PRICE_MODES.RRP,
        value: null,
    },

    // This is the currently selected print area list
    selectedPrintAreas = [],

    // This is the shipping destination list
    shippingDestinations = [],

    // This is the initial payment handling mode
    initialUserTakesPayment = true,

    // This is the rate card data
    rateCard,

    // This controls whether the helper text is shown inline
    displayInfo = false,

    // This is the store home country code
    storeHomeCountry = "Unknown",

    // This is the user's account default currency
    accountDefaultCurrency = "GBP",

    // This is the core countries API response
    coreCountries = [],

    // This is the core currencies API response
    coreCurrencies = {},

    // This fires when the price config changes
    onPriceConfigChange,

    // Flag if the price editor should be disabled
    disablePriceEditor,

    podDesignDetails,

    // This is the default colourway code stored on the design product
    defaultColourwayCode = "",

    // All service codes - for fulfilment shipping costs
    serviceCodes,

    // This contains API-calculated values for the visible product price display
    apiDisplayValues = null,

    // This says whether API-calculated values are loading, ready or unavailable
    apiDisplayStatus = "idle",

}) => {


    // Get the token from AuthContext
    const { token } = useContext(AuthContext);

    // Get user's account data 
    const {
        connectionsData,
        fulfilmentCountries
    } = useContext(UserContext);

    // Store the prices used for the currently selected destination tax rate
    const [destinationPrices, setDestinationPrices] = useState(defaultPrices);

    // Store the selected shipping country
    const [selectedShippingCountry, setSelectedShippingCountry] = useState("");

    // Store the selected shipping method code
    const [selectedShippingMethodCode, setSelectedShippingMethodCode] = useState("");

    // Store the selected earnings preview currency
    const [selectedEarningsCurrencyCode, setSelectedEarningsCurrencyCode] = useState("");

    // Store the API earnings preview response
    const [earningsPreviewResponse, setEarningsPreviewResponse] = useState(null);

    // Store the API earnings preview status
    const [earningsPreviewStatus, setEarningsPreviewStatus] = useState(EARNINGS_PREVIEW_STATUS.IDLE);

    // Store the API earnings preview error
    const [earningsPreviewError, setEarningsPreviewError] = useState("");

    // Store which earning preview sections are open
    const [openEarningPreviewSections, setOpenEarningPreviewSections] = useState({});

    // Get the current selected store connection
    const selectedStoreConnection = connectionsData?.find((connection) => connection.id === podDesignDetails.store_id) || null;
    

    // Memo the available currencies based on the store settings 'currencies' mapped to the core currencies data with an account currency fallback
    const availableCurrencies = useMemo(() => {

        // Get store details
        const storeDetails = selectedStoreConnection || {};

        // Find the currencies setting
        const currenciesSetting = storeDetails?.settings?.find(
            (setting) => setting.type === "Pricing" && setting.code === "currencies"
        );

        // Build selected currency list
        let selectedCurrencyCodes = (currenciesSetting?.value || "")
            .split(",")
            .map((currencyCode) => currencyCode.trim())
            .filter(Boolean);

        // If no currencies are configured, default to the account currency
        if (selectedCurrencyCodes.length === 0 && accountDefaultCurrency) {

            selectedCurrencyCodes = [accountDefaultCurrency];

        }

        // Create Set for faster lookups
        const selectedCurrencySet = new Set(selectedCurrencyCodes);

        // Return currencies not already selected
        return Object.entries(coreCurrencies || {})
            .filter(([currencyCode]) => selectedCurrencySet.has(currencyCode))
            .map(([currencyCode, currencyDetails]) => ({
                code: currencyCode,
                ...currencyDetails,
            }));

    }, [selectedStoreConnection, coreCurrencies, accountDefaultCurrency ]);



    // Keep destination prices synced with default prices until a destination-specific call replaces them
    useEffect(() => {

        // Store the default prices as the starting destination prices
        setDestinationPrices(defaultPrices);

    }, [
        defaultPrices,
    ]);



    const resolvedVatRate = useMemo(() => {

        const destinationTaxRate = getTaxRateFromDestinationPrices({
            destinationPrices,
            sku,
            childSku,
        });

        if (destinationTaxRate > 0) return destinationTaxRate;

        const defaultTaxRate = getTaxRateFromPrices(defaultPrices);

        if (defaultTaxRate > 0) return defaultTaxRate;

        return 0;

    }, [
        destinationPrices,
        defaultPrices,
        sku,
        childSku,
    ]);


    // Resolve the fallback price config
    const fallbackPriceConfig = useMemo(() => {

        // Return the provided initial config when available
        return initialPriceConfig || {
            mode: PRICE_MODES.RRP,
            value: null,
        };

    }, [
        initialPriceConfig,
    ]);


    // Store the active price config
    const [priceConfig, setPriceConfig] = useState(() => {

        // Prefer the product's saved prices on first mount
        return getPriceConfigFromProductPrices({
            productPrices,
            fallbackPriceConfig,
            resolvedVatRate,
        });

    });


    // Store the price warning
    const [priceWarning, setPriceWarning] = useState("");

    // Store whether the merchant takes payment themselves
    const [userTakesPayment, setUserTakesPayment] = useState(Boolean(initialUserTakesPayment));

    // Store the popup state
    const [showPriceEditorPopUp, setShowPriceEditorPopUp] = useState(false);

    // Store the selected shipping destination value
    const [selectedDestinationValue, setSelectedDestinationValue] = useState("");



    // Resolve the normalized account currency
    const accountCurrencyCode = useMemo(() => {

        // Return the normalized account currency
        return String(accountDefaultCurrency || "GBP").toUpperCase();

    }, [
        accountDefaultCurrency,
    ]);



    // Resolve the store default currency
    const storeCurrencyCode = useMemo(() => {

        // Return the resolved store currency
        return getStoreDefaultCurrency({
            storeHomeCountry,
            coreCountries,
            fallbackCurrency: accountCurrencyCode || "GBP",
        });

    }, [
        storeHomeCountry,
        coreCountries,
        accountCurrencyCode,
    ]);



    // Resolve the display currency
    const displayCurrencyCode = useMemo(() => {

        // Display using the store's default currency, else account default currency, else GBP
        return  storeCurrencyCode || accountCurrencyCode || "GBP";

    }, [
        accountCurrencyCode,
        storeCurrencyCode,
    ]);



    // Resolve the currently selected destination object
    const selectedDestination = useMemo(() => {

        // Read the safe destinations
        const safeDestinations = getSafeShippingDestinations(shippingDestinations);

        // Return the matched destination when one is selected
        if (selectedDestinationValue) {

            return findShippingDestination(shippingDestinations, selectedDestinationValue);

        }

        // Return the destination that matches the stores home country
        return safeDestinations.find((destination) => destination.code === storeHomeCountry) || null;

    }, [
        shippingDestinations,
        selectedDestinationValue,
        storeHomeCountry,
    ]);



    // Fetch destination-specific prices when the shipping destination changes
    useEffect(() => {

        // Stop when the API function is missing
        if (!getPodProductPrices) return;

        // Stop when the token is missing
        if (!token) return;

        // Read the selected destination country code
        const countryCode = selectedDestination?.code;

        // Stop when there is no country code
        if (!countryCode) return;

        // Use the already-loaded default prices for the store home country
        if (countryCode === storeHomeCountry) {

            setDestinationPrices(defaultPrices);
            return;

        }

        // Store whether this request is still active
        let isActive = true;

        // Fetch prices for the selected destination
        const fetchDestinationPrices = async () => {

            try {

                // Request POD prices for the selected country
                const nextPrices = await getPodProductPrices(token, countryCode);

                // Stop if the request is no longer active
                if (!isActive) return;

                // Store the destination prices
                setDestinationPrices(nextPrices);

            } catch (error) {

                // Stop if the request is no longer active
                if (!isActive) return;

                // Fall back to the default prices
                setDestinationPrices(defaultPrices);

            }

        };

        // Run the fetch
        fetchDestinationPrices();

        // Mark old requests as inactive
        return () => {

            // Prevent stale requests from updating state
            isActive = false;

        };

    }, [
        token,
        selectedDestination,
        defaultPrices,
        storeHomeCountry,
    ]);



    // Set the first shipping destination by default
    useEffect(() => {

        // Read the safe destinations
        const safeDestinations = getSafeShippingDestinations(shippingDestinations);

        // Stop when there are no destinations
        if (safeDestinations?.length === 0) return;

        // Set the first destination when nothing is selected
        if (!selectedDestinationValue) {

            // Set selected destination to the one that matches the stores home country - find the matching destination
            const defaultSelectedDestinationValue = safeDestinations?.find((destination) => destination.code === storeHomeCountry)?.name || "";

            // Update the state
            setSelectedDestinationValue(defaultSelectedDestinationValue);

        }

    }, [
        shippingDestinations,
        selectedDestinationValue,
        storeHomeCountry
    ]);



    // Reset local price state only when a different product is being edited
    useEffect(() => {

        // Prefer the product's saved prices for the newly selected product
        const nextPriceConfig = getPriceConfigFromProductPrices({
            productPrices,
            fallbackPriceConfig,
            resolvedVatRate,
        });

        // Only update state when the config actually changed
        setPriceConfig((currentPriceConfig) => {

            // Keep the current state when both configs match
            if (priceConfigsAreSame(currentPriceConfig, nextPriceConfig)) {
                return currentPriceConfig;
            }

            // Otherwise use the next config
            return nextPriceConfig;

        });

        // Clear warning
        setPriceWarning((currentWarning) => {

            // Keep the current warning if it is already blank
            if (currentWarning === "") return currentWarning;

            // Otherwise clear the warning
            return "";

        });

    // Deliberately only reset when product identity changes.
    // productPrices changes after onPriceConfigChange, so it must NOT be a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        sku,
        childSku,
    ]);



    // Build the available shipping countries from active shipping methods
    const availableShippingCountries = useMemo(() => {

        // Return the available shipping countries
        return getAvailableShippingCountries(selectedStoreConnection);

    }, [
        selectedStoreConnection,
    ]);



    // Build the available shipping methods for the selected country
    const availableShippingMethods = useMemo(() => {

        // Return methods for the selected country
        return getShippingMethodsForCountry({
            selectedStoreConnection,
            selectedShippingCountry,
        });

    }, [
        selectedStoreConnection,
        selectedShippingCountry,
    ]);



    // Resolve the selected shipping method
    const selectedShippingMethod = useMemo(() => {

        // Return the matched method
        return availableShippingMethods.find((shippingMethod) => shippingMethod?.code === selectedShippingMethodCode) || availableShippingMethods[0] || null;

    }, [
        availableShippingMethods,
        selectedShippingMethodCode,
    ]);



    // Resolve the customer shipping price in the display currency
    const selectedShippingPrice = useMemo(() => {

        // Return the shipping price for the display currency
        return getShippingPriceForCurrency({
            shippingMethod: selectedShippingMethod,
            currencyCode: displayCurrencyCode,
        });

    }, [
        selectedShippingMethod,
        displayCurrencyCode,
    ]);


    // Set default earnings preview currency
    useEffect(() => {

        // Stop when there are no currencies
        if (!availableCurrencies || availableCurrencies.length === 0) { return; }

        // Keep the selected currency if it is still available
        if (availableCurrencies.some((currency) => currency.code === selectedEarningsCurrencyCode)) { return; }

        // Prefer the store currency when available
        const matchedStoreCurrency = availableCurrencies.find((currency) => currency.code === storeCurrencyCode);

        // Set the selected preview currency
        setSelectedEarningsCurrencyCode(matchedStoreCurrency?.code || availableCurrencies[0]?.code || "");

    }, [
        availableCurrencies,
        selectedEarningsCurrencyCode,
        storeCurrencyCode,
    ]);



    // Set default shipping country
    useEffect(() => {

        // Stop when there are no shipping countries
        if (availableShippingCountries.length === 0) return;

        // Stop when a country is already selected
        if (selectedShippingCountry) return;

        // Prefer the selected destination name if it exists in shipping countries
        const matchedDestinationCountry = availableShippingCountries.includes(selectedDestinationValue) ? selectedDestinationValue : "";

        // Get the country from fulfilmnet countries that matches the store's home country code
        const defaultStoreCountryName = fulfilmentCountries?.find((country) => country.code === storeHomeCountry)?.name;

        // Set the default shipping country
        setSelectedShippingCountry(matchedDestinationCountry || defaultStoreCountryName || availableShippingCountries[0]);

    }, [availableShippingCountries, selectedShippingCountry, selectedDestinationValue, fulfilmentCountries, storeHomeCountry]);



    // Set default shipping method
    useEffect(() => {

        // Stop when there are no available methods
        if (availableShippingMethods.length === 0) return;

        // Keep selected method if it is still available
        if (availableShippingMethods.some((shippingMethod) => shippingMethod?.code === selectedShippingMethodCode)) return;

        // Set the first available method
        setSelectedShippingMethodCode(availableShippingMethods[0]?.code || "");

    }, [
        availableShippingMethods,
        selectedShippingMethodCode,
    ]);



    // Find the matched product from the fetched rate card
    const matchedProduct = useMemo(() => {

        // Return the matched product
        return findRateCardProductBySku(rateCard, sku, childSku);

    }, [
        rateCard,
        sku,
        childSku,
    ]);


    // Build the current plan raw name
    const currentPlanRawName = useMemo(() => {

        // Return the current plan name
        return getPlanNameByLevel(currentPlanLevel, subscriptionLevels);

    }, [
        currentPlanLevel,
        subscriptionLevels,
    ]);



    // Resolve the sale price based on mode/currency/RRP
    const resolvedSalePrice = useMemo(() => {

        // Return the resolved sale price
        return getResolvedSalePrice({
            priceConfig,
            rrpPrices: defaultPrices,
            storeCurrencyCode,
            displayCurrencyCode,
            coreCurrencies,
            product: matchedProduct,
            currentPlanLevel,
            selectedPrintAreas,
            subscriptionLevels,
            userTakesPayment,
            resolvedVatRate,
            destination: selectedDestination,
            accountDefaultCurrency: accountCurrencyCode,
            serviceCodes,
            selectedShippingCountry,
            selectedShippingMethod,
            selectedShippingPrice,
        });

    }, [priceConfig, defaultPrices, storeCurrencyCode, displayCurrencyCode, coreCurrencies, matchedProduct, currentPlanLevel, selectedPrintAreas, subscriptionLevels, userTakesPayment, resolvedVatRate, selectedDestination, accountCurrencyCode, serviceCodes, selectedShippingCountry, selectedShippingMethod, selectedShippingPrice]);



    // Calculate the current plan outcome
    const currentPlanOutcome = useMemo(() => {

        // Return empty values when the product is missing
        if (!matchedProduct || !currentPlanRawName) {

            // Return empty outcome
            return {
                productionCost: 0,
                totalCost: 0,
                tax: 0,
                platformFeeRate: 0,
                paymentFeeRate: 0,
                platformFee: 0,
                paymentFee: 0,
                fees: 0,
                profit: 0,
                profitInAccountCurrency: 0,
                marginPercent: 0,
            };

        }

        // Return the current plan outcome
        return getSaleOutcome({
            product: matchedProduct,
            level: currentPlanLevel,
            salePrice: resolvedSalePrice,
            selectedPrintAreas,
            subscriptionLevels,
            userTakesPayment,
            resolvedVatRate,
            destination: selectedDestination,
            displayCurrencyCode,
            accountDefaultCurrency: accountCurrencyCode,
            coreCurrencies,
            serviceCodes,
            selectedShippingCountry,
            selectedShippingMethod,
            shippingPrice: selectedShippingPrice,
        });

    }, [matchedProduct, currentPlanRawName, currentPlanLevel, resolvedSalePrice, selectedPrintAreas, subscriptionLevels, userTakesPayment, resolvedVatRate, selectedDestination, displayCurrencyCode, accountCurrencyCode, coreCurrencies, serviceCodes, selectedShippingCountry, selectedShippingMethod, selectedShippingPrice]);



    // This is the minimum valid sale price for the current plan in the display currency
    const currentPlanMinimumSalePrice = useMemo(() => {

        // Return zero when the product is missing
        if (!matchedProduct) return 0;

        // Return the minimum valid sale price
        return getMinimumSalePrice({
            product: matchedProduct,
            level: currentPlanLevel,
            selectedPrintAreas,
            subscriptionLevels,
            userTakesPayment,
            resolvedVatRate,
            destination: selectedDestination,
            displayCurrencyCode,
            coreCurrencies,
            serviceCodes,
            selectedShippingCountry,
            selectedShippingMethod,
            selectedShippingPrice,
        });

    }, [matchedProduct, currentPlanLevel, selectedPrintAreas, subscriptionLevels, userTakesPayment, resolvedVatRate, selectedDestination, displayCurrencyCode, coreCurrencies, serviceCodes, selectedShippingCountry, selectedShippingMethod, selectedShippingPrice]);



    // Convert the minimum baseline price into store currency
    const minimumBaselinePrice = useMemo(() => {

        // Convert current display minimum into the store currency
        return convertCurrency({
            value: currentPlanMinimumSalePrice,
            fromCurrencyCode: displayCurrencyCode,
            toCurrencyCode: storeCurrencyCode,
            coreCurrencies,
        });

    }, [
        currentPlanMinimumSalePrice,
        displayCurrencyCode,
        storeCurrencyCode,
        coreCurrencies,
    ]);

    

    // Calculate the API earnings preview whenever the selected options change
    useEffect(() => {

        // Stop when the popup is closed
        if (!showPriceEditorPopUp) { return; }

        // Stop when there is no token
        if (!token) { return; }

        // Stop when there is no selected store
        if (!selectedStoreConnection?.id) { return; }

        // Stop when there is no selected shipping country
        if (!selectedShippingCountry) { return; }

        // Stop when there is no selected shipping method
        if (!selectedShippingMethod?.code) { return; }

        // Stop when there is no selected currency
        if (!selectedEarningsCurrencyCode) { return; }

        // Work out the configured store SKU
        const orderItemStoreSku = storeSku || "";

        // Stop when there is no configured store SKU
        if (!orderItemStoreSku) { return; }

        // Work out the POD product SKU
        const podProductSku = childSku || matchedProduct?.sku || sku;

        // Stop when there is no POD product SKU
        if (!podProductSku) { return; }

        // Stop when there is no design set
        if (!podDesignDetails?.design_set_id) { return; }

        // Build the mock order payload
        const orderPayload = buildEarningsPreviewOrderPayload({
            connectionId: selectedStoreConnection.id,
            deliveryCountry: selectedShippingCountry,
            shippingMethodCode: selectedShippingMethod.code,
            currencyCode: selectedEarningsCurrencyCode,
            storeSku: orderItemStoreSku,
            podProductSku,
            designSetId: podDesignDetails.design_set_id,
            priceConfig,
            colourwayCode: defaultColourwayCode,
        });

        // Store whether this request is still active
        let requestIsActive = true;

        // Mark the preview as loading
        setEarningsPreviewStatus(EARNINGS_PREVIEW_STATUS.LOADING);
        setEarningsPreviewError("");

        // Call the API
        calculateOrderEarnings(token, orderPayload).then((response) => {

            // Stop if the request is stale
            if (!requestIsActive) { return; }

            // Store API failures
            if (!response?.success) {
                setEarningsPreviewResponse(null);
                setEarningsPreviewStatus(EARNINGS_PREVIEW_STATUS.ERROR);
                setEarningsPreviewError(response?.error_message || response?.error || "Unable to calculate earnings.");
                return;
            }

            // Store the API response
            setEarningsPreviewResponse(response.results);
            setEarningsPreviewStatus(EARNINGS_PREVIEW_STATUS.READY);
            setEarningsPreviewError("");

        }).catch((error) => {

            // Stop if the request is stale
            if (!requestIsActive) { return; }

            // Store the error response
            setEarningsPreviewResponse(null);
            setEarningsPreviewStatus(EARNINGS_PREVIEW_STATUS.ERROR);
            setEarningsPreviewError(error?.message || "Unable to calculate earnings.");

        });

        // Cancel stale responses
        return () => {

            // Mark this request as stale
            requestIsActive = false;

        };

    }, [
        showPriceEditorPopUp,
        token,
        selectedStoreConnection,
        selectedShippingCountry,
        selectedShippingMethod,
        selectedEarningsCurrencyCode,
        storeSku,
        childSku,
        matchedProduct,
        sku,
        podDesignDetails,
        priceConfig,
        defaultColourwayCode,
    ]);



    // Commit price config changes to the parent
    const handleCommitPriceConfig = (nextPriceConfig) => {

        // Build the backend-ready payload
        const payload = buildPricePayload({
            priceConfig: nextPriceConfig,
            storeCurrencyCode,
        });

        // Notify the parent when available
        if (onPriceConfigChange) {

            // Send payload to parent
            onPriceConfigChange(payload);

        }

    };



    // Toggle an API earnings preview detail section
    const handleToggleEarningPreviewSection = (sectionKey) => {

        // Toggle the selected section
        setOpenEarningPreviewSections((previousOpenSections) => ({
            ...previousOpenSections,
            [sectionKey]: !previousOpenSections[sectionKey],
        }));

    };

    

    // Render loading state when rate card data is missing
    if (!rateCard) {

        // Return loading card
        return (
            <section className="product-price-editor">
                <h3 className="product-price-editor__title">Store Pricing</h3>
                <p className="product-price-editor__helper">Loading pricing data...</p>
            </section>
        );

    }



    // Render empty state when product is not found
    if (!matchedProduct) {

        // Return empty card
        return (

            <section className="product-price-editor">

                <p className="product-price-editor__helper">

                    Pricing data is not available for this product yet.

                </p>

            </section>

        );

    }



    // This is the shared display cost
    const displayCost = currentPlanOutcome.totalCost;

    // This is the shared display profit

    // This is the API-calculated inline store price
    const inlineDisplaySalePrice = apiDisplayValues?.storePrice ?? null;

    // This is the API-calculated inline product earning
    const inlineDisplayProfit = apiDisplayValues?.totalEarning ?? null;

    // This is the API-calculated inline currency
    const inlineDisplayCurrencyCode = apiDisplayValues?.currencyCode || displayCurrencyCode;

    // This is the inline API display status
    let inlineDisplayStatus = "loading";

    // Use ready as soon as the API preview request has completed
    if (apiDisplayStatus === "ready") {

        inlineDisplayStatus = "ready";

    }

    // Use unavailable when the API display failed or cannot be run
    if (apiDisplayStatus === "error" || apiDisplayStatus === "unavailable") {

        inlineDisplayStatus = "unavailable";

    }

    // Get the API earning preview for the user's current subscription level
    const getCurrentPlanEarningsPreview = () => {

        // Read the API previews safely
        const earningPreviews = earningsPreviewResponse?.earning_previews || {};

        // Read the preview list
        const previewEntries = Object.entries(earningPreviews);

        // Get the current subscription level row
        const currentSubscriptionLevel = getSubscriptionLevelByLevel(currentPlanLevel, subscriptionLevels);

        // Read the current subscription code
        const currentSubscriptionCode = String(currentSubscriptionLevel?.code || "").trim();

        // Match by subscription code when available
        if (currentSubscriptionCode !== "") {

            // Find the preview with the matching subscription code
            const matchedCodePreviewEntry = previewEntries.find(([previewCode, preview]) => {

                // Read the preview subscription code
                const previewSubscriptionCode = String(preview?.subscription_level?.code || previewCode || "").trim();

                // Match the subscription code
                return previewSubscriptionCode === currentSubscriptionCode;

            });

            // Return the matched preview
            if (matchedCodePreviewEntry) { return matchedCodePreviewEntry[1]; }

        }

        // Fall back to matching by numeric subscription level
        const matchedLevelPreviewEntry = previewEntries.find(([previewCode, preview]) => {

            // Match the current subscription level
            return Number(preview?.subscription_level?.level) === Number(currentPlanLevel);

        });

        // Return the matched preview when found
        if (matchedLevelPreviewEntry) { return matchedLevelPreviewEntry[1]; }

        // Return null when there is no match
        return null;

    };



    // Render one money value from the API response
    const renderApiMoney = (value) => {

        // Return the formatted value
        return FormatCurrency(   
            selectedEarningsCurrencyCode,
            toNumber(value),
        );


    };



    // Render one money value from the API response
    const renderApiMoneyInAccountCurrency = (value) => {

        // Return the formatted value
        return FormatCurrency(  
            accountCurrencyCode,
            toNumber(value),
        );

    };



    // Render one API earnings matrix value row
    const renderApiEarningsMatrixRow = (label, previewEntries, getValue, extraClassName = "", sectionKey = "", infoText = "", minusFigure = false) => {

        // Work out whether this row can be expanded
        const rowCanExpand = sectionKey !== "";

        // Read whether the row is open
        const isOpen = rowCanExpand && Boolean(openEarningPreviewSections[sectionKey]);

        // Build the row label content
        const rowLabelContent = (

            <>

                <span className="product-price-editor__api-matrix-label-text">

                    <span>
                        {label}
                    </span>

                    {infoText !== "" && (

                        <InfoButton
                            shortLabel=""
                            description={infoText}
                        />

                    )}

                </span>

                {rowCanExpand && (

                    <span className="product-price-editor__api-matrix-row-toggle">
                        {isOpen ? "−" : "+"}
                    </span>

                )}

            </>

        );

        // Return the matrix row
        return (

            <div key={label} className={`product-price-editor__api-matrix-row ${extraClassName}`}>

                {rowCanExpand ? (

                    <button
                        type="button"
                        className="product-price-editor__api-matrix-label product-price-editor__api-matrix-label--clickable"
                        onClick={() => handleToggleEarningPreviewSection(sectionKey)}
                    >
                        {rowLabelContent}
                        
                    </button>

                ) : (

                    <div className="product-price-editor__api-matrix-label">
                        {rowLabelContent}
                    </div>

                )}

                {previewEntries.map(([previewCode, preview]) => (

                    <div
                        key={`${label}-${previewCode}-${preview}`}
                        className="product-price-editor__api-matrix-value"
                    >
                        {minusFigure && "-"}{rowCanExpand && isOpen ? "" : label === "Fulfilment" ? renderApiMoneyInAccountCurrency(getValue(preview)) : renderApiMoney(getValue(preview))}

                    </div>

                ))}

            </div>

        );

    };



    // Render the detail rows attached to an earning row
    const renderApiEarningsMatrixDetails = (sectionKey, previewEntries, rows) => {

        // Stop when the section is closed
        if (!openEarningPreviewSections[sectionKey]) { return null; }

        if (sectionKey === "product-detail") {

            return renderGrossProductBreakdown(previewEntries);

        }

        // Return the detail rows
        return rows.map((row) => {

            // Build the row class name
            let rowClassName = row.total === true ? "product-price-editor__api-matrix-row--detail-total" : "product-price-editor__api-matrix-row--detail";

            // Add an extra row class when supplied
            if (row.className) {
                rowClassName = `${rowClassName} ${row.className}`;
            }

            

            // Return the matrix row
            return renderApiEarningsMatrixRow(
                row.label,
                previewEntries,
                row.getValue,
                rowClassName,
                "",
                row.infoText || "",
                row.minusFigure,
            );

        });

    };


    const renderGrossProductBreakdown = (previewEntries) => {

        return (

            <>

                {renderApiEarningsMatrixRow(
                    "Product Gross",
                    previewEntries,
                    (preview) => preview?.earning_breakdown?.order?.product?.original?.gross,
                    "",
                    "product-gross"
                )}

                {renderApiEarningsMatrixDetails(
                    "product-gross",
                    previewEntries,
                    [
                        {
                            label: "Store Price (exc. tax)",
                            getValue: (preview) => preview?.customer_pricing?.product_price_exc_tax,
                        },
                        {
                            label: "Tax",
                            getValue: (preview) => preview?.earning_breakdown?.order?.product?.original?.taxes?.total,
                            minusFigure: true,
                        },
                        {
                            label: "Store Price (inc. tax)",
                            getValue: (preview) => preview?.customer_pricing?.product_price_inc_tax,
                            className: "product-price-editor__api-matrix-row--customer-price",
                            infoText: "This is the product price shown to the customer before checkout charges.",
                        },
                        {
                            label: "Import charges",
                            getValue: (preview) => preview?.earning_breakdown?.order?.product?.original?.import_charges?.total,
                            infoText: "These are added at checkout where import charges apply.",
                        },
                        {
                            label: "Product gross",
                            getValue: (preview) => preview?.earning_breakdown?.order?.product?.original?.gross,
                        },
                    ]
                )}

            </>
            
        )

    };



    // Render the API earnings breakdown using a table
    const RenderApiEarningsBreakdownTable = (previewEntries) => {

        //console.log("previewEntries: ", previewEntries);

        // Return nothing when there are no preview columns
        if (!Array.isArray(previewEntries) || previewEntries.length === 0) {

            // Do not render an empty table
            return null;

        }

        // Check whether the selected preview currency differs from the account currency
        const requiresAccountCurrencyConversion =
            String(selectedEarningsCurrencyCode || "").toUpperCase() !==
            String(accountCurrencyCode || "").toUpperCase();

        // Read whether the product earning section is open
        const productSectionIsOpen = Boolean(openEarningPreviewSections["product-detail"]);

        // Read whether the product gross section is open
        const productGrossSectionIsOpen = Boolean(openEarningPreviewSections["product-gross"]);

        // Read whether the product fulfilment section is open
        const productFulfilmentSectionIsOpen = Boolean(openEarningPreviewSections["product-fulfilment-detail"]);

        // Read whether the shipping earning section is open
        const shippingSectionIsOpen = Boolean(openEarningPreviewSections["shipping-detail"]);

        // Read whether the total earning section is open
        const totalSectionIsOpen = Boolean(openEarningPreviewSections["order-detail"]);

        // Resolve the current subscription code
        const currentSubscriptionCode = String(
            getSubscriptionLevelByLevel(
                currentPlanLevel,
                subscriptionLevels
            )?.code || ""
        ).trim();

        // Check whether a preview column represents the user's current plan
        const previewIsCurrentPlan = (previewCode, preview) => {

            // Read the preview subscription code
            const previewSubscriptionCode = String(
                preview?.subscription_level?.code || previewCode || ""
            ).trim();

            // Match using the subscription code when both codes are available
            if (
                currentSubscriptionCode !== "" &&
                previewSubscriptionCode !== ""
            ) {

                // Return whether the subscription codes match
                return previewSubscriptionCode === currentSubscriptionCode;

            }

            // Fall back to matching the numeric subscription level
            return Number(preview?.subscription_level?.level) === Number(currentPlanLevel);

        };

        // Read conversion details for one earnings section
        const getSectionConversionDetails = (
            preview,
            sectionKey
        ) => {

            // Read the order-level breakdown safely
            const orderBreakdown =
                preview
                    ?.earning_breakdown
                    ?.order || {};

            // Read the requested section safely
            const sectionBreakdown =
                orderBreakdown
                    ?.[sectionKey] || {};

            // Return normalized conversion information
            return {
                originalCurrency:
                    orderBreakdown
                        ?.original_currency || selectedEarningsCurrencyCode,

                convertedCurrency:
                    orderBreakdown
                        ?.converted_currency || accountCurrencyCode,

                exchangeRate:
                    orderBreakdown
                        ?.exchange_rate ?? 1,

                originalNet:
                    sectionBreakdown
                        ?.original
                        ?.net ?? 0,

                convertedNet:
                    sectionBreakdown
                        ?.converted
                        ?.net ?? 0,
            };

        };

        // Format a conversion rate without a currency symbol
        const renderConversionRate = (value) => {

            // Convert the rate into a safe number
            const safeRate = toNumber(value);

            // Return the rate with up to four decimal places
            return safeRate.toFixed(4);

        };

        // Highlight every cell belonging to a hovered subscription column
        const handlePreviewColumnMouseEnter = (event, previewCode) => {

            // Find the parent table
            const tableElement = event.currentTarget.closest("table");

            // Stop when the table cannot be found
            if (!tableElement) return;

            // Find every cell belonging to this preview column
            const columnCells = tableElement.querySelectorAll(
                `[data-preview-code="${String(previewCode)}"]`
            );

            // Add the appropriate hovered class to every column cell
            columnCells.forEach((columnCell) => {

                // Check whether this is a header cell
                const isHeaderCell = columnCell.tagName === "TH";

                // Add the header hover class
                if (isHeaderCell) {

                    // Highlight the plan header
                    columnCell.classList.add(
                        "product-price-editor__comparison-plan-header--hovered"
                    );

                    // Stop this iteration
                    return;

                }

                // Highlight a normal value cell
                columnCell.classList.add(
                    "product-price-editor__comparison-value-cell--hovered"
                );

            });

        };

        // Remove column highlighting when the pointer leaves
        const handlePreviewColumnMouseLeave = (event, previewCode) => {

            // Find the parent table
            const tableElement = event.currentTarget.closest("table");

            // Stop when the table cannot be found
            if (!tableElement) return;

            // Find every cell belonging to this preview column
            const columnCells = tableElement.querySelectorAll(
                `[data-preview-code="${String(previewCode)}"]`
            );

            // Remove both possible hovered classes
            columnCells.forEach((columnCell) => {

                // Remove the plan header hover class
                columnCell.classList.remove(
                    "product-price-editor__comparison-plan-header--hovered"
                );

                // Remove the normal value-cell hover class
                columnCell.classList.remove(
                    "product-price-editor__comparison-value-cell--hovered"
                );

            });

        };

        // This renders the heading cell for a comparison-table row
        const renderRowHeader = ({
            label,
            infoText = "",
            isExpandable = false,
            isExpanded = false,
            onToggle = null,
            nestingLevel = 0,
            total=false,
        }) => {

            // Calculate the indentation applied to the content inside the heading cell
            const nestingIndent = `${nestingLevel * 20}px`;

            // Render the row heading
            return (

                // Use a semantic heading cell for the row
                <th
                    className="product-price-editor__comparison-row-header"
                    scope="row"
                >

                    {/* Render an interactive heading when the row can expand */}
                    {isExpandable ? (

                        // Use a button so the row can be operated with a keyboard
                        <button
                            className="product-price-editor__comparison-row-header-button"
                            type="button"
                            onClick={onToggle}
                            aria-expanded={isExpanded}
                            style={{
                                paddingLeft: nestingIndent,
                            }}
                        >

                            {/* Render the open or closed arrow */}
                            <span
                                className={`product-price-editor__comparison-arrow ${
                                    isExpanded
                                        ? "product-price-editor__comparison-arrow--open"
                                        : ""
                                }`}
                                aria-hidden="true"
                            >
                                {isExpanded ? "-" : "+"}
                            </span>

                            {/* Render the row label */}
                            <span className="product-price-editor__comparison-row-label">
                                {label}
                            </span>

                            {/* Render optional supplementary information */}
                            {infoText && (

                                // Display the information next to the row label
                                <InfoButton shortLabel={label} description={infoText}  />

                            )}

                        </button>

                    ) : (

                        // Wrap non-interactive content so only its contents are indented
                        <span
                            className={total ? "product-price-editor__comparison-row-header-total" : "product-price-editor__comparison-row-header-content" }
                            style={{
                                paddingLeft: nestingIndent,
                            }}
                        >

                            {/* Render the row label */}
                            <span className="product-price-editor__comparison-row-label">
                                {label}
                            </span>

                            {/* Render optional supplementary information */}
                            {infoText && (

                                // Display the information next to the row label
                                <InfoButton shortLabel={label} description={infoText}  />

                            )}

                        </span>

                    )}

                </th>

            );

        };

        // Render one value cell for a preview column
        const renderValueCell = ({
            previewCode,
            preview,
            value,
            formatter = renderApiMoney,
            nested = false,
            currentPlan = false,
            profit = false,
            extraClassName = "",
            prefix = "",
        }) => {

            // Build the value-cell class name
            const valueCellClassName = [
                "product-price-editor__comparison-value-cell",
                nested
                    ? "product-price-editor__comparison-value-cell--nested"
                    : "",
                currentPlan
                    ? "product-price-editor__comparison-value-cell--current"
                    : "",
                profit
                    ? "product-price-editor__comparison-value-cell--profit"
                    : "",
                extraClassName,
            ]
                .filter(Boolean)
                .join(" ");

            // Return the formatted table value
            return (

                // Render one subscription value cell
                <td
                    key={previewCode}
                    data-preview-code={previewCode}
                    className={valueCellClassName}
                    onMouseEnter={(event) => {
                        handlePreviewColumnMouseEnter(event, previewCode);
                    }}
                    onMouseLeave={(event) => {
                        handlePreviewColumnMouseLeave(event, previewCode);
                    }}
                >
                    {value === "hide" ? 
                    
                        "" 
                        
                        : 
                        
                        <>
                            {prefix} {formatter(value, preview)}
                        </>
                        
                    }

                </td>

            );

        };

        // This renders one complete comparison-table row
        const renderTableRow = ({
            rowKey,
            label,
            getValue,
            sectionKey = "",
            isExpandable = false,
            isExpanded = false,
            onToggle = null,
            nestingLevel = 0,
            nested = false,
            profit = false,
            total = false,
            prefix = "",
            formatter = renderApiMoney,
            extraValueClassName = "",
            infoText = "",
            rowClassName = "",
        }) => {

            // Treat a supplied section key as an expandable row
            const rowIsExpandable = Boolean(sectionKey) || isExpandable;

            // Read the expansion state from the shared section state when a section key exists
            const rowIsExpanded = sectionKey
                ? Boolean(openEarningPreviewSections[sectionKey])
                : isExpanded;

            // Toggle the shared section state when a section key exists
            const handleRowToggle = sectionKey
                ? () => {
                    handleToggleEarningPreviewSection(sectionKey);
                }
                : onToggle;

            // Build the row class name
            const tableRowClassName = [
                "product-price-editor__comparison-row",
                nested
                    ? "product-price-editor__comparison-row--nested"
                    : "",
                total
                    ? "product-price-editor__comparison-row--total"
                    : "",
                profit
                    ? "product-price-editor__comparison-row--profit"
                    : "",
                rowClassName,
            ]
                .filter(Boolean)
                .join(" ");

            // Return the completed table row
            return (

                // Render one semantic table row
                <tr
                    key={rowKey}
                    className={tableRowClassName}
                >

                    {/* Render the row heading */}
                    {renderRowHeader({
                        label,
                        infoText,
                        isExpandable: rowIsExpandable,
                        isExpanded: rowIsExpanded,
                        onToggle: handleRowToggle,
                        nestingLevel,
                        total,
                    })}

                    {/* Render one value for every subscription preview */}
                    {previewEntries.map(([previewCode, preview]) => {

                        // Check whether this column represents the current plan
                        const currentPlan = previewIsCurrentPlan(
                            previewCode,
                            preview
                        );

                        // Render the value cell
                        return renderValueCell({
                            previewCode,
                            preview,
                            value: rowIsExpanded ? "hide" : getValue(preview),
                            formatter,
                            nested,
                            currentPlan,
                            profit,
                            prefix,
                            extraClassName: [
                                total
                                    ? "product-price-editor__comparison-value-cell--total"
                                    : "",
                                extraValueClassName,
                            ]
                                .filter(Boolean)
                                .join(" "),
                        });

                    })}

                </tr>

            );

        };

        // Return the complete earnings comparison table
        return (

            // Allow horizontal scrolling on smaller screens
            <div className="product-price-editor__comparison-table-wrap">

                {/* Render a semantic earnings comparison table */}
                <table className="product-price-editor__comparison-table">

                    {/* Render the subscription headings */}
                    <thead>

                        {/* Render the heading row */}
                        <tr>

                            {/* Render the top-left heading */}
                            <th
                                scope="col"
                                className="product-price-editor__comparison-corner-cell"
                            >
                                Breakdown
                            </th>

                            {/* Render one heading per subscription plan */}
                            {previewEntries.map(([previewCode, preview]) => {

                                // Read whether this is the current plan
                                const currentPlan = previewIsCurrentPlan(
                                    previewCode,
                                    preview
                                );

                                // Build the plan-header class name
                                const planHeaderClassName = [
                                    "product-price-editor__comparison-plan-header",
                                    currentPlan
                                        ? "product-price-editor__comparison-plan-header--current"
                                        : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ");

                                // Return the plan heading
                                return (

                                    // Render a semantic column heading
                                    <th
                                        key={`header-${previewCode}`}
                                        scope="col"
                                        data-preview-code={previewCode}
                                        className={planHeaderClassName}
                                        onMouseEnter={(event) => {
                                            handlePreviewColumnMouseEnter(
                                                event,
                                                previewCode
                                            );
                                        }}
                                        onMouseLeave={(event) => {
                                            handlePreviewColumnMouseLeave(
                                                event,
                                                previewCode
                                            );
                                        }}
                                    >

                                        {/* Lay out the plan heading */}
                                        <span className="product-price-editor__comparison-plan-heading">

                                            {/* Render the plan name with dot */}
                                            <span className={`pod-pricing-page__plan-dot pod-pricing-page__plan-dot--${preview?.subscription_level?.code || "default"}`}></span>
                                            <span className="product-price-editor__comparison-plan-name">
                                                {preview?.subscription_level?.name || previewCode}
                                            </span>

                                        </span>

                                    </th>

                                );

                            })}

                        </tr>

                    </thead>

                    {/* Render the customer pricing and earnings rows */}
                    <tbody>

                        {/* Render the customer-facing product price for each subscription level */}
                        {renderTableRow({
                            rowKey: "customer-product-price",
                            label: "Product price",
                            getValue: (preview) => preview?.customer_pricing?.product_price_inc_tax,
                        })}

                        {/* Render the top-level product earning */}
                        {renderTableRow({
                            rowKey: "product-earning",
                            label: "Product earning",
                            sectionKey: "product-detail",
                            getValue: (preview) => preview?.product_earning,
                            profit: true,
                            formatter: requiresAccountCurrencyConversion
                                ? renderApiMoneyInAccountCurrency
                                : renderApiMoney,
                        })}

                        {/* Render the product breakdown when open */}
                        {productSectionIsOpen && (

                            <>

                                {/* Render the expandable product gross row */}
                                {renderTableRow({
                                    rowKey: "product-gross",
                                    label: "Product gross",
                                    isExpandable: true,
                                    isExpanded: productGrossSectionIsOpen,
                                    profit: true,
                                    onToggle: () => {
                                        handleToggleEarningPreviewSection("product-gross");
                                    },
                                    nested: true,
                                    nestingLevel: 1,
                                    getValue:  
                                        (preview) => (
                                            preview
                                                ?.earning_breakdown
                                                ?.order
                                                ?.product
                                                ?.original
                                                ?.gross
                                        ),
                                })}

                                {/* Render the gross breakdown when open */}
                                {productGrossSectionIsOpen && (

                                    <>

                                        {/* Render the product price excluding tax */}
                                        {renderTableRow({
                                            rowKey: "product-price-excluding-tax",
                                            label: "Store price excluding tax",
                                            nested: true,
                                            nestingLevel: 2,
                                            getValue: (preview) => preview?.customer_pricing?.product_price_exc_tax,
                                        })}

                                        {/* Render product tax */}
                                        {renderTableRow({
                                            rowKey: "product-tax",
                                            label: "Tax",
                                            nested: true,
                                            nestingLevel: 2,
                                            prefix: "+",
                                            getValue: (preview) => (
                                                preview
                                                    ?.earning_breakdown
                                                    ?.order
                                                    ?.product
                                                    ?.original
                                                    ?.taxes
                                                    ?.total
                                            ),
                                        })}

                                        {/* Render the customer-facing product price */}
                                        {renderTableRow({
                                            rowKey: "product-price-including-tax",
                                            label: "Store price including tax",
                                            nested: true,
                                            nestingLevel: 2,
                                            infoText: "This is the product price shown to the customer before checkout charges.",
                                            getValue: (preview) => preview?.customer_pricing?.product_price_inc_tax,
                                        })}

                                        {/* Render import charges */}
                                        {renderTableRow({
                                            rowKey: "product-import-charges",
                                            label: "Import charges",
                                            nested: true,
                                            nestingLevel: 2,
                                            prefix: "+",
                                            infoText: "These are added at checkout where import charges apply.",
                                            getValue: (preview) => (
                                                preview
                                                    ?.earning_breakdown
                                                    ?.order
                                                    ?.product
                                                    ?.original
                                                    ?.import_charges
                                                    ?.total
                                            ),
                                        })}

                                        {/* Repeat the resulting product gross total */}
                                        {renderTableRow({
                                            rowKey: "product-gross-total",
                                            label: "Product gross",
                                            nested: true,
                                            nestingLevel: 2,
                                            total: true,
                                            profit: true,
                                            getValue: (preview) => (
                                                preview
                                                    ?.earning_breakdown
                                                    ?.order
                                                    ?.product
                                                    ?.original
                                                    ?.gross
                                            ),
                                        })}

                                    </>

                                )}

                                {/* Render product tax */}
                                {renderTableRow({
                                    rowKey: "product-tax",
                                    label: "Tax",
                                    nested: true,
                                    nestingLevel: 1,
                                    prefix: "−",
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.product
                                            ?.original
                                            ?.taxes
                                            ?.total
                                    ),
                                })}

                                {/* Render import charges */}
                                {renderTableRow({
                                    rowKey: "product-import-charges",
                                    label: "Import charges",
                                    nested: true,
                                    nestingLevel: 1,
                                    prefix: "−",
                                    infoText: "These are added at checkout where import charges apply.",
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.product
                                            ?.original
                                            ?.import_charges
                                            ?.total
                                    ),
                                })}

                                {/* Render product platform fees */}
                                {renderTableRow({
                                    rowKey: "product-platform-fee",
                                    label: "Platform fee",
                                    nested: true,
                                    nestingLevel: 1,
                                    prefix: "−",
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.product
                                            ?.original
                                            ?.fees
                                            ?.platform
                                            ?.total
                                    ),
                                })}

                                {/* Render product payment fees */}
                                {renderTableRow({
                                    rowKey: "product-payment-fee",
                                    label: "Payment fee",
                                    nested: true,
                                    nestingLevel: 1,
                                    prefix: "−",
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.product
                                            ?.original
                                            ?.fees
                                            ?.payment
                                            ?.total
                                    ),
                                })}

                                {/* Render the product total before conversion */}
                                {requiresAccountCurrencyConversion && renderTableRow({
                                    rowKey: "product-conversion-total",
                                    label: `Total in ${selectedEarningsCurrencyCode}`,
                                    nested: true,
                                    nestingLevel: 3,
                                    getValue: (preview) => {

                                        // Read the conversion data for this plan
                                        const conversionDetails =
                                            getSectionConversionDetails(preview, "product");

                                        // Return the original product net
                                        return conversionDetails.originalNet;

                                    },
                                })}

                                {/* Render the exchange rate */}
                                {requiresAccountCurrencyConversion && renderTableRow({
                                    rowKey: "product-conversion-rate",
                                    label: `${selectedEarningsCurrencyCode} → ${accountCurrencyCode} conversion rate`,
                                    nested: true,
                                    nestingLevel: 3,
                                    formatter: renderConversionRate,
                                    getValue: (preview) => {

                                        // Read the conversion data for this plan
                                        const conversionDetails =
                                            getSectionConversionDetails(preview, "product");

                                        // Return the API exchange rate
                                        return conversionDetails.exchangeRate;

                                    },
                                })}

                                {/* Render the converted value in account currency */}
                                {requiresAccountCurrencyConversion && renderTableRow({
                                    rowKey: "product-value-after-conversion",
                                    label: `Value after conversion to ${accountCurrencyCode}`,
                                    nested: true,
                                    nestingLevel: 3,
                                    formatter: renderApiMoneyInAccountCurrency,
                                    getValue: (preview) => {

                                        // Read the conversion data for this plan
                                        const conversionDetails =
                                            getSectionConversionDetails(preview, "product");

                                        // Return the converted product net
                                        return conversionDetails.convertedNet;

                                    },
                                })}

                                {/* Render the expandable product fulfilment total */}
                                {renderTableRow({

                                    // Give the row a unique React key
                                    rowKey: "product-fulfilment",

                                    // Show the account currency when fulfilment differs from the selected store currency
                                    label: `Fulfilment${
                                        requiresAccountCurrencyConversion
                                            ? ` (${accountCurrencyCode})`
                                            : ""
                                    }`,

                                    // Connect this row to the shared expandable-section state
                                    sectionKey: "product-fulfilment-detail",

                                    // Display this underneath the main product earning breakdown
                                    nested: true,

                                    // Indent the fulfilment row by one level
                                    nestingLevel: 1,

                                    // Show fulfilment as a deduction
                                    prefix: "−",

                                    // Fulfilment is returned in account currency when conversion is required
                                    formatter: requiresAccountCurrencyConversion
                                        ? renderApiMoneyInAccountCurrency
                                        : renderApiMoney,

                                    // Read the complete fulfilment total from the API
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.product
                                            ?.deductions
                                            ?.fulfilment
                                            ?.total
                                    ),

                                })}

                                {/* Render the product fulfilment breakdown when open */}
                                {productFulfilmentSectionIsOpen && (

                                    <>

                                        {/* Render the fulfilment base product cost */}
                                        {renderTableRow({

                                            // Give the row a unique React key
                                            rowKey: "product-fulfilment-base",

                                            // Display the cost description
                                            label: "Base price",

                                            // Display this underneath fulfilment
                                            nested: true,

                                            // Indent one level further than the fulfilment parent row
                                            nestingLevel: 2,

                                            // Fulfilment values are costs deducted from the earning
                                            prefix: "−",

                                            // Fulfilment is returned in account currency when conversion is required
                                            formatter: requiresAccountCurrencyConversion
                                                ? renderApiMoneyInAccountCurrency
                                                : renderApiMoney,

                                            // Read the item's base fulfilment cost from the API
                                            getValue: (preview) => (
                                                preview
                                                    ?.earning_breakdown
                                                    ?.order
                                                    ?.product
                                                    ?.deductions
                                                    ?.fulfilment
                                                    ?.order_items
                                                    ?.[0]
                                                    ?.item_base
                                            ),

                                        })}

                                        {/* Render the fulfilment printing cost */}
                                        {renderTableRow({

                                            // Give the row a unique React key
                                            rowKey: "product-fulfilment-print",

                                            // Display the cost description
                                            label: "Print price",

                                            // Display this underneath fulfilment
                                            nested: true,

                                            // Indent one level further than the fulfilment parent row
                                            nestingLevel: 2,

                                            // Fulfilment values are costs deducted from the earning
                                            prefix: "−",

                                            // Fulfilment is returned in account currency when conversion is required
                                            formatter: requiresAccountCurrencyConversion
                                                ? renderApiMoneyInAccountCurrency
                                                : renderApiMoney,

                                            // Read the item's printing fulfilment cost from the API
                                            getValue: (preview) => (
                                                preview
                                                    ?.earning_breakdown
                                                    ?.order
                                                    ?.product
                                                    ?.deductions
                                                    ?.fulfilment
                                                    ?.order_items
                                                    ?.[0]
                                                    ?.item_print
                                            ),

                                        })}

                                        {/* Render the resulting fulfilment total */}
                                        {renderTableRow({

                                            // Give the row a unique React key
                                            rowKey: "product-fulfilment-total",

                                            // Display the total description
                                            label: "Fulfilment total",

                                            // Display this underneath fulfilment
                                            nested: true,

                                            // Indent one level further than the fulfilment parent row
                                            nestingLevel: 2,

                                            // Give the row the existing total styling
                                            total: true,

                                            // Fulfilment values are costs deducted from the earning
                                            prefix: "−",

                                            // Fulfilment is returned in account currency when conversion is required
                                            formatter: requiresAccountCurrencyConversion
                                                ? renderApiMoneyInAccountCurrency
                                                : renderApiMoney,

                                            // Read the authoritative fulfilment total from the API
                                            getValue: (preview) => (
                                                preview
                                                    ?.earning_breakdown
                                                    ?.order
                                                    ?.product
                                                    ?.deductions
                                                    ?.fulfilment
                                                    ?.total
                                            ),

                                        })}

                                    </>

                                )}

                                {/* Render the resulting product earning */}
                                {renderTableRow({
                                    rowKey: "product-earning-total",
                                    label: "Product earning",
                                    nested: true,
                                    nestingLevel: 1,
                                    total: true,
                                    profit: true,
                                    formatter: requiresAccountCurrencyConversion
                                        ? renderApiMoneyInAccountCurrency
                                        : renderApiMoney,
                                    getValue: (preview) => preview?.product_earning,
                                })}

                            </>

                        )}

                        {/* Render the top-level shipping earning */}
                        {renderTableRow({
                            rowKey: "shipping-earning",
                            label: "Shipping earning",
                            sectionKey: "shipping-detail",
                            getValue: (preview) => preview?.shipping_earning,
                            profit: true,
                            formatter: requiresAccountCurrencyConversion
                                ? renderApiMoneyInAccountCurrency
                                : renderApiMoney,
                        })}

                        {/* Render the shipping earning breakdown */}
                        {shippingSectionIsOpen && (

                            <>

                                {/* Render shipping gross */}
                                {renderTableRow({
                                    rowKey: "shipping-gross",
                                    label: "Shipping gross",
                                    nested: true,
                                    nestingLevel: 1,
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.shipping
                                            ?.original
                                            ?.gross
                                    ),
                                })}

                                {/* Render shipping tax */}
                                {renderTableRow({
                                    rowKey: "shipping-tax",
                                    label: "Shipping tax",
                                    nested: true,
                                    nestingLevel: 1,
                                    prefix: "−",
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.shipping
                                            ?.original
                                            ?.taxes
                                            ?.total
                                    ),
                                })}

                                {/* Render shipping platform fee */}
                                {renderTableRow({
                                    rowKey: "shipping-platform-fee",
                                    label: "Platform fee",
                                    nested: true,
                                    nestingLevel: 1,
                                    prefix: "−",
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.shipping
                                            ?.original
                                            ?.fees
                                            ?.platform
                                            ?.total
                                    ),
                                })}

                                {/* Render shipping payment fee */}
                                {renderTableRow({
                                    rowKey: "shipping-payment-fee",
                                    label: "Payment fee",
                                    nested: true,
                                    nestingLevel: 1,
                                    prefix: "−",
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.shipping
                                            ?.original
                                            ?.fees
                                            ?.payment
                                            ?.total
                                    ),
                                })}

                                {/* Render the shipping total before conversion */}
                                {requiresAccountCurrencyConversion && renderTableRow({
                                    rowKey: "shipping-conversion-total",
                                    label: `Total in ${selectedEarningsCurrencyCode}`,
                                    nested: true,
                                    nestingLevel: 3,
                                    getValue: (preview) => {

                                        // Read the conversion data for this plan
                                        const conversionDetails =
                                            getSectionConversionDetails(preview, "shipping");

                                        // Return the original product net
                                        return conversionDetails.originalNet;

                                    },
                                })}

                                {/* Render the exchange rate */}
                                {requiresAccountCurrencyConversion && renderTableRow({
                                    rowKey: "shipping-conversion-rate",
                                    label: `${selectedEarningsCurrencyCode} → ${accountCurrencyCode} conversion rate`,
                                    nested: true,
                                    nestingLevel: 3,
                                    formatter: renderConversionRate,
                                    getValue: (preview) => {

                                        // Read the conversion data for this plan
                                        const conversionDetails =
                                            getSectionConversionDetails(preview, "shipping");

                                        // Return the API exchange rate
                                        return conversionDetails.exchangeRate;

                                    },
                                })}

                                {/* Render the converted value in account currency */}
                                {requiresAccountCurrencyConversion && renderTableRow({
                                    rowKey: "shipping-value-after-conversion",
                                    label: `Value after conversion to ${accountCurrencyCode}`,
                                    nested: true,
                                    nestingLevel: 3,
                                    formatter: renderApiMoneyInAccountCurrency,
                                    getValue: (preview) => {

                                        // Read the conversion data for this plan
                                        const conversionDetails =
                                            getSectionConversionDetails(preview, "shipping");

                                        // Return the converted product net
                                        return conversionDetails.convertedNet;

                                    },
                                })}

                                {/* Render shipping fulfilment */}
                                {renderTableRow({
                                    rowKey: "shipping-fulfilment",
                                    label: "Fulfilment",
                                    nested: true,
                                    nestingLevel: 1,
                                    prefix: "−",
                                    formatter: requiresAccountCurrencyConversion
                                        ? renderApiMoneyInAccountCurrency
                                        : renderApiMoney,
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.shipping
                                            ?.deductions
                                            ?.fulfilment
                                            ?.total
                                    ),
                                })}

                                {/* Render resulting shipping earning */}
                                {renderTableRow({
                                    rowKey: "shipping-earning-total",
                                    label: "Shipping earning",
                                    nested: true,
                                    nestingLevel: 1,
                                    total: true,
                                    profit: true,
                                    formatter: requiresAccountCurrencyConversion
                                        ? renderApiMoneyInAccountCurrency
                                        : renderApiMoney,
                                    getValue: (preview) => preview?.shipping_earning,
                                })}

                            </>

                        )}

                        {/* Render the complete order earning */}
                        {renderTableRow({
                            rowKey: "total-earning",
                            label: "Total earning",
                            sectionKey: "order-detail",
                            total: true,
                            profit: true,
                            formatter: requiresAccountCurrencyConversion
                                ? renderApiMoneyInAccountCurrency
                                : renderApiMoney,
                            getValue: (preview) => preview?.total_earning,
                        })}

                        {/* Render the complete order breakdown */}
                        {totalSectionIsOpen && (

                            <>

                                {/* Render the complete order gross */}
                                {renderTableRow({
                                    rowKey: "order-gross",
                                    label: "Order gross",
                                    nested: true,
                                    nestingLevel: 1,
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.totals
                                            ?.original
                                            ?.gross
                                    ),
                                })}

                                {/* Render the complete order tax */}
                                {renderTableRow({
                                    rowKey: "order-tax",
                                    label: "Order tax",
                                    nested: true,
                                    nestingLevel: 1,
                                    prefix: "−",
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.totals
                                            ?.original
                                            ?.taxes
                                            ?.total
                                    ),
                                })}

                                {/* Render total import charges */}
                                {renderTableRow({
                                    rowKey: "order-import-charges",
                                    label: "Import charges",
                                    nested: true,
                                    nestingLevel: 1,
                                    prefix: "−",
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.totals
                                            ?.original
                                            ?.import_charges
                                            ?.total
                                    ),
                                })}

                                {/* Render total fees */}
                                {renderTableRow({
                                    rowKey: "order-fees",
                                    label: "Fees",
                                    nested: true,
                                    nestingLevel: 1,
                                    prefix: "−",
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.totals
                                            ?.original
                                            ?.fees
                                            ?.total
                                    ),
                                })}

                                {/* Render the product total before conversion */}
                                {requiresAccountCurrencyConversion && renderTableRow({
                                    rowKey: "product-conversion-total",
                                    label: `Total in ${selectedEarningsCurrencyCode}`,
                                    nested: true,
                                    nestingLevel: 3,
                                    getValue: (preview) => {

                                        // Read the conversion data for this plan
                                        const conversionDetails =
                                            getSectionConversionDetails(preview, "totals");

                                        // Return the original product net
                                        return conversionDetails.originalNet;

                                    },
                                })}

                                {/* Render the exchange rate */}
                                {requiresAccountCurrencyConversion && renderTableRow({
                                    rowKey: "product-conversion-rate",
                                    label: `${selectedEarningsCurrencyCode} → ${accountCurrencyCode} conversion rate`,
                                    nested: true,
                                    nestingLevel: 3,
                                    formatter: renderConversionRate,
                                    getValue: (preview) => {

                                        // Read the conversion data for this plan
                                        const conversionDetails =
                                            getSectionConversionDetails(preview, "totals");

                                        // Return the API exchange rate
                                        return conversionDetails.exchangeRate;

                                    },
                                })}

                                {/* Render the converted value in account currency */}
                                {requiresAccountCurrencyConversion && renderTableRow({
                                    rowKey: "product-value-after-conversion",
                                    label: `Value after conversion to ${accountCurrencyCode}`,
                                    nested: true,
                                    nestingLevel: 3,
                                    formatter: renderApiMoneyInAccountCurrency,
                                    getValue: (preview) => {

                                        // Read the conversion data for this plan
                                        const conversionDetails =
                                            getSectionConversionDetails(preview, "totals");

                                        // Return the converted product net
                                        return conversionDetails.convertedNet;

                                    },
                                })}

                                {/* Render total fulfilment */}
                                {renderTableRow({
                                    rowKey: "order-fulfilment",
                                    label: "Fulfilment",
                                    nested: true,
                                    nestingLevel: 1,
                                    prefix: "−",
                                    formatter: requiresAccountCurrencyConversion
                                        ? renderApiMoneyInAccountCurrency
                                        : renderApiMoney,
                                    getValue: (preview) => (
                                        preview
                                            ?.earning_breakdown
                                            ?.order
                                            ?.totals
                                            ?.deductions
                                            ?.fulfilment
                                            ?.total
                                    ),
                                })}

                                {/* Render the resulting complete earning */}
                                {renderTableRow({
                                    rowKey: "order-total-earning",
                                    label: "Total earning",
                                    nested: true,
                                    nestingLevel: 1,
                                    total: true,
                                    profit: true,
                                    formatter: requiresAccountCurrencyConversion
                                        ? renderApiMoneyInAccountCurrency
                                        : renderApiMoney,
                                    getValue: (preview) => preview?.total_earning,
                                })}

                            </>

                        )}

                    </tbody>

                </table>

            </div>

        );

    };



    // Render the API-driven earnings preview
    const renderApiEarningsPreview = () => {

        // Read whether we have a usable API result
        const hasPreviewResponse = Boolean(earningsPreviewResponse);

        // Read the API previews
        const earningPreviews = earningsPreviewResponse?.earning_previews || {};

        // Read the preview rows
        const previewEntries = Object.entries(earningPreviews);

        // Return the API preview panel
        return (

            <div className={`product-price-editor__api-preview ${earningsPreviewStatus === EARNINGS_PREVIEW_STATUS.LOADING ? "product-price-editor__api-preview--loading" : ""}`}>

                <div className="product-price-editor__api-overview">

                    <div className="product-price-editor__api-controls">

                        <select
                            className="product-price-editor__destination-select"
                            value={selectedShippingCountry}
                            onChange={(event) => setSelectedShippingCountry(event.target.value)}
                        >
                            {availableShippingCountries.map((country) => (

                                <option
                                    key={country}
                                    value={country}
                                >
                                    {country}
                                </option>

                            ))}
                        </select>

                        <select
                            className="product-price-editor__destination-select"
                            value={selectedShippingMethod?.code || ""}
                            onChange={(event) => setSelectedShippingMethodCode(event.target.value)}
                        >
                            {availableShippingMethods.map((shippingMethod) => (

                                <option
                                    key={`${shippingMethod?.code}-${shippingMethod?.name}`}
                                    value={shippingMethod?.code}
                                >
                                    {shippingMethod?.name}
                                </option>

                            ))}
                        </select>

                        <select
                            className="product-price-editor__destination-select"
                            value={selectedEarningsCurrencyCode}
                            onChange={(event) => setSelectedEarningsCurrencyCode(event.target.value)}
                        >
                            {availableCurrencies.map((currency) => (

                                <option
                                    key={currency.code}
                                    value={currency.code}
                                >
                                    {currency.code}
                                </option>

                            ))}
                        </select>

                    </div>

                </div>

                <div className="product-price-editor__api-preview-body">

                    {earningsPreviewStatus === EARNINGS_PREVIEW_STATUS.LOADING && (

                        <div className="product-price-editor__api-recalculating">
                            Recalculating…
                        </div>

                    )}

                    {earningsPreviewStatus === EARNINGS_PREVIEW_STATUS.ERROR && (

                        <div className="product-price-editor__api-message product-price-editor__api-message--error">
                            {earningsPreviewError}
                        </div>

                    )}

                    {/* 
                    {hasPreviewResponse && renderApiEarningsPreviewMatrix(previewEntries)}
                    */}
                       
                    {earningsPreviewStatus === EARNINGS_PREVIEW_STATUS.LOADING ? 

                        <LoadingSpinner message={!hasPreviewResponse && earningsPreviewStatus !== EARNINGS_PREVIEW_STATUS.ERROR && "Earnings will appear here once the first calculation completes."} />

                        : hasPreviewResponse ? RenderApiEarningsBreakdownTable(previewEntries)
                    
                            : null

                    }

                    {!hasPreviewResponse && earningsPreviewStatus !== EARNINGS_PREVIEW_STATUS.ERROR && earningsPreviewStatus !== EARNINGS_PREVIEW_STATUS.LOADING && (

                        <div className="product-price-editor__api-placeholder">
                            Earnings will appear here once the first calculation completes.
                        </div>

                    )}

                </div>

            </div>

        );

    };



    // Render the full editor
    const renderFullEditor = () => {

        // Get the API preview for the current user plan
        const currentApiPlanPreview = getCurrentPlanEarningsPreview();

        // Use the current subscription preview product price in the popup summary
        const popupDisplaySalePrice =
            earningsPreviewStatus === EARNINGS_PREVIEW_STATUS.READY
                ? currentApiPlanPreview?.customer_pricing?.product_price_inc_tax ?? null
                : null;

        // Use the complete API order earning in the popup summary
        const popupDisplayProfit =
            earningsPreviewStatus === EARNINGS_PREVIEW_STATUS.READY
                ? currentApiPlanPreview?.total_earning ?? null
                : null;

        // Use the selected API currency in the popup once available
        const popupDisplayCurrencyCode = selectedEarningsCurrencyCode || displayCurrencyCode;

        // Set the popup display status
        let popupDisplayValuesStatus = "loading";

        // Mark popup display as ready when the API preview is ready
        if (earningsPreviewStatus === EARNINGS_PREVIEW_STATUS.READY) {

            popupDisplayValuesStatus = "ready";

        }

        // Mark popup display as unavailable when the API preview failed
        if (earningsPreviewStatus === EARNINGS_PREVIEW_STATUS.ERROR) {

            popupDisplayValuesStatus = "unavailable";

        }

        // Return full editor
        return (

            <div className="product-price-editor">

                <div className="product-price-editor__summary">

                    <div className="product-price-editor__summary-copy">

                        <p className="product-price-editor__helper">
                            Set the store pricing method and value to instantly see what you make per sale.
                        </p>

                    </div>

                    <ProductPriceEditorSummaryMetrics
                        priceConfig={priceConfig}
                        setPriceConfig={setPriceConfig}
                        displaySalePrice={popupDisplaySalePrice}
                        displayCost={displayCost}
                        displayProfit={popupDisplayProfit}
                        displayCurrencyCode={popupDisplayCurrencyCode}
                        displayValuesStatus={popupDisplayValuesStatus}
                        profitCurrencyCode={accountCurrencyCode}
                        storeCurrencyCode={storeCurrencyCode}
                        coreCurrencies={coreCurrencies}
                        minimumBaselinePrice={minimumBaselinePrice}
                        priceWarning={priceWarning}
                        setPriceWarning={setPriceWarning}
                        onCommitPriceConfig={handleCommitPriceConfig}
                        isFullEditor={true}
                        userTakesPayment={userTakesPayment}
                        displayCostCard={true}
                        disablePriceEditor={disablePriceEditor}
                    />

                </div>

                <br />

                <div className="product-price-editor__body">

                    <div className="product-price-editor__footnotes">

                        <p className="product-price-editor__footnote">
                            Earnings are calculated by the API using the same mock order flow as checkout, including tax mode, import duty, shipping, fees and fulfilment deductions.
                        </p>

                    </div>

                    {renderApiEarningsPreview()}

                </div>

            </div>

        );

    };



    // Return inline price editor with info button to open popup
    return (

        <>

            {displayInfo ? (

                <>

                    <div className="product-price-editor__summary-copy">

                        <p className="product-price-editor__helper">
                            Set the store pricing method and value to instantly see what you make per sale.
                        </p>

                    </div>

                    <div className="product-price-editor__summary">

                        <ProductPriceEditorSummaryMetrics
                            priceConfig={priceConfig}
                            setPriceConfig={setPriceConfig}
                            displaySalePrice={inlineDisplaySalePrice}
                            displayCost={displayCost}
                            displayProfit={inlineDisplayProfit}
                            displayCurrencyCode={inlineDisplayCurrencyCode}
                            displayValuesStatus={inlineDisplayStatus}
                            profitCurrencyCode={accountCurrencyCode}
                            storeCurrencyCode={storeCurrencyCode}
                            coreCurrencies={coreCurrencies}
                            minimumBaselinePrice={minimumBaselinePrice}
                            priceWarning={priceWarning}
                            setPriceWarning={setPriceWarning}
                            onCommitPriceConfig={handleCommitPriceConfig}
                            setShowPriceEditorPopUp={setShowPriceEditorPopUp}
                            userTakesPayment={userTakesPayment}
                            displayCostCard={true}
                            disablePriceEditor={disablePriceEditor}

                        />

                    </div>

                </>

            ) : (

                <ProductPriceEditorSummaryMetrics
                    priceConfig={priceConfig}
                    setPriceConfig={setPriceConfig}
                    displaySalePrice={inlineDisplaySalePrice}
                    displayCost={displayCost}
                    displayProfit={inlineDisplayProfit}
                    displayCurrencyCode={inlineDisplayCurrencyCode}
                    displayValuesStatus={inlineDisplayStatus}
                    profitCurrencyCode={accountCurrencyCode}
                    storeCurrencyCode={storeCurrencyCode}
                    coreCurrencies={coreCurrencies}
                    minimumBaselinePrice={minimumBaselinePrice}
                    priceWarning={priceWarning}
                    setPriceWarning={setPriceWarning}
                    onCommitPriceConfig={handleCommitPriceConfig}
                    setShowPriceEditorPopUp={setShowPriceEditorPopUp}
                    userTakesPayment={userTakesPayment}
                    displayCostCard={false}
                    disablePriceEditor={disablePriceEditor}

                />

            )}

            {showPriceEditorPopUp && (

                <div className="popup-overlay">

                    <div
                        className="popup-container popup-container--medium"
                        
                    >

                        <div className="popup__header">

                            <div className="popup__headerLeft">

                                <h2 className="popup__title">
                                    Store Pricing
                                </h2>

                                <p className="popup__subtitle">
                                    Store's home country is {storeHomeCountry} • Fulfilment costs shown in account currency {accountCurrencyCode}
                                </p>

                            </div>

                            <div className="popup__headerRight">

                                <button
                                    type="button"
                                    className="popup__close"
                                    onClick={() => {

                                        // Reset payment mode
                                        setUserTakesPayment(Boolean(initialUserTakesPayment));

                                        // Close popup
                                        setShowPriceEditorPopUp(false);

                                    }}
                                    title="Close"
                                >
                                    <CloseIcon />
                                </button>

                            </div>

                        </div>

                        <div className="popup-content popup-content__body">
                            {renderFullEditor()}
                        </div>

                    </div>

                </div>

            )}

        </>

    );

};



// Export default component
export default ProductPriceEditor;